import './DiagramSelection.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Check, ZoomIn, ZoomOut, X, List, ChevronDown, ChevronLeft, ChevronRight, Search, Map as MapIcon, Layers, Hash, ListFilter, Square, Box, Boxes, Sparkles, RefreshCw } from 'lucide-react';
import { useOrder } from '../../contexts/OrderContext';
import { TranslationBatchManager } from '../../services/TranslationService';
import { DIAGRAM_TAG, DIAGRAM_PALETTE, CalloutPriority, diagramTagMetrics, resolveTagOverlaps, matchLevel, matchTagStyle } from '../../utils/diagramTag';
import DiagramRefChip from './DiagramRefChip';
import MatchStrength from './MatchStrength';
import { catalogKits, catalogPartIdentity, isCatalogKit } from '../../utils/catalogKits';
import { positionChipsFromExtra, positionClassesFromName, catalogDetailEntries, normalizeDetailKey, humanizeDetailKey, displayPartRef, partDescription, PositionClasses, CatalogDetail } from '../../utils/partLabel';
import { supersessionNumbers, isSupersessionDetailKey } from '../../utils/partSupersession';
import { sheetsFrom, sheetOfCalloutMap, sheetOfRow as sheetOfRowFn, openingSheet } from '../../utils/diagramSheets';
import { cardTags, kitHeaderTags, withoutTags, filterTags, passesFilters, toggleFilterId, filterAxisOf, functionOf, sameFunction, mentionsFunction, searchMatches, vocabularyTagsOf, filterId, cardAnswersFilter, PartTag, AiRowTags, RowAxes } from '../../utils/partTags';
import PartTagRow, { FilterChips, tagText } from './diagram/PartTagRow';
import CategorySwitcher, { CategoryItem } from './diagram/CategorySwitcher';

// ── The ONE fullscreen parts-diagram viewer ─────────────────────────────────
// Used by the clarification question (StructuredQuestionForm →
// ClarificationDiagramInline) and by the search-result card (ImageWithCallouts).
// Both hand it the same thing: an image with callout hotspots and a flat list of
// catalog rows already resolved to "what this row means" (candidate or not,
// score, selected, in cart…). The viewer owns everything visual — the tags on
// the drawing, the legend, the zoom/pan, the parts list sorted by estimate, the
// translations — so the two surfaces cannot drift. What differs between them
// (the header strip, the footer actions, a per-row action, a note under a
// label) comes in as render slots.

export interface DiagramCalloutBox {
  label: string;
  bounds: { x: number; y: number; w: number; h: number };
  normalized?: boolean;
}

/** One drawing of a section. A section ships several: on 32/335 sheet 1 holds
 *  the triangular wishbones and sheet 2 the longitudinal arms, so a viewer that
 *  shows one sheet hides half the answers to "which arm type?". */
export interface DiagramSheet {
  index: number;
  id?: string | null;
  /** Per-sheet identity from the drawing id (PV000.001.567.475). NOT the
   *  group·section code, which is identical on every sheet of a section. */
  code?: string | null;
  /** The drawing's own title, when the vendor has one. */
  title?: string | null;
  diagram_url: string | null;
  callouts: DiagramCalloutBox[];
  part_callouts?: string[];
}

export interface DiagramSource {
  diagram_url: string | null;
  callouts: DiagramCalloutBox[];
  /** Absent on older payloads → the viewer synthesises a single sheet. */
  sheets?: DiagramSheet[] | null;
  sheet_count?: number | null;
  primary_sheet?: number | null;
  section_name?: string | null;
  group_name?: string | null;
}

/** Another section the resolver thinks may hold the requested part. */
export interface DiagramCandidateSection {
  group_id: string;
  section_id: string;
  name: string;
  parent_group?: string | null;
  sheet_count?: number | null;
  count?: number | null;
  current?: boolean;
}

/** catax_3d job stages → what the progress bar says (locale key chat.clarification.modelStage.<stage>). */
const MODEL_STAGE_TEXT: Record<string, string> = {
  queued: 'waiting for a worker', parsing: 'reading the drawing', rendering: 'redrawing the sheet in 3D',
  segmenting: 'cutting the parts out', modeling: 'generating the parts', connecting: 'assembling', assembling: 'writing the model',
};

export interface DiagramRow {
  /** Unique per row (a callout instance of one catalog part). */
  key: string;
  callout: string;
  partId: string | null;
  /** Raw manufacturer number; the viewer applies displayPartRef. */
  partNumber: string | null;
  /** Raw catalog name (English); translated by the viewer. */
  name: string | null;
  description?: string | null;
  extra_details?: Record<string, string> | null;
  /** The backend's reading of the row (side, position, …); see RowAxes. */
  axes?: RowAxes | null;
  score: number | null;
  /** Lives in the candidate block (else under "Other parts in this diagram"). */
  candidate: boolean;
  priority: CalloutPriority;
  /** This surface's own selection: navy tag + halo, navy row bar. */
  selected: boolean;
  /** Chosen in an earlier question of the same form: navy ✓ badge on the tag,
   *  hidden from the list. */
  prior?: boolean;
  inCart?: boolean;
  /** A person chose this part (result rows stamped `user_selected`). */
  userPick?: boolean;
  /** A person chose this part (answered the question, or clicked it): the
   *  list shows no match estimate on it. */
  confirmed?: boolean;
  quantity?: string | number | null;
  /** Rows sharing this key are ONE physical part at several callouts (a
   *  bundle) — listed once, every tag lights together. Defaults to `key`. */
  bundleKey?: string;
  /** Sheet this row's callout is drawn on. Rows off the active sheet show a
   *  "Sheet N" chip, and selecting one switches the diagram. */
  sheet?: number | null;
  sheets?: number[] | null;
  /** The caller's own record, handed back untouched from onToggleRow. */
  raw: any;
}

export interface DiagramHeaderCtx {
  /** Is answer `n` the active filter? */
  answerActive: (n: number, label: string) => boolean;
  /** Filter the list to answer `n`'s parts (again: clear). False when no
   *  part carries the answer — nothing to filter by. */
  toggleAnswer: (n: number, label: string) => boolean;
  /** The tags of every selected card (as the list shows them), so a header
   *  can repeat the chosen part's facts beside the request. */
  selectedTags: { key: string; tags: PartTag[] }[];
}

export interface DiagramLegendExtra {
  kind: 'prior' | 'userPick' | 'inCart';
  label: string;
}

export interface DiagramModalProps {
  open: boolean;
  /** Translator, when the caller already has one (the question form passes its
   *  own). Falls back to useTranslation, then to the English default. */
  t?: (key: string, opts?: any) => string;
  /** null = still loading (the shell stays up with a spinner). */
  diagram: DiagramSource | null;
  /** Every catalog row on the drawing, in CATALOG order (kits are detected
   *  from adjacency). Prior rows are tagged but not listed. */
  rows: DiagramRow[];
  onClose: () => void;
  /** Select / deselect a row — from its list row, its tag, or the keyboard.
   *  Also called with `false` for a kit whose component was just chosen (and
   *  vice versa), since the two are alternatives. */
  onToggleRow: (row: DiagramRow, next: boolean) => void;
  /** Shape of the row's selection control: radio (single) or box (multi). */
  selectionMode?: 'single' | 'multi';
  /** View-only: no selection changes, tags inert. */
  disabled?: boolean;
  /** Amber "choose one of N parts to continue" on a candidate label that
   *  carries several part numbers (the question needs an answer; results don't). */
  /** @deprecated The amber "choose one of N" banner was removed — the card and
   *  its radio rows carry that meaning. Accepted so callers need no change. */
  chooseOneNudge?: boolean;
  /** Start with "Other parts in this diagram" open. A question wants the
   *  escape hatch visible (the user may be hunting for their part); a result
   *  already has its answer, and on a bumper sheet that group is 43 bolts and
   *  clips that would push the answer off the top. Default: open. */
  otherPartsOpen?: boolean;
  /** What the user asked for — feeds the "why this estimate" evidence. */
  requestTexts?: string[];
  /** Translations already fetched by the caller (original → translated). */
  preTranslations?: Record<string, string> | null;
  legendExtras?: DiagramLegendExtra[];
  /** Scroll to + flash a row (e.g. a candidate named in the AI comparison);
   *  `nonce` changes per request so the same row can be re-focused. */
  focusRequest?: { key: string; nonce: number } | null;
  /** Section + parent group, shown as a breadcrumb pill INSIDE the viewport
   *  (it used to live in the caller's header strip). */
  sectionName?: string | null;
  groupName?: string | null;
  /** The catalog's own ids for the section ("81 / 060"), shown as the category's code. */
  sectionCode?: string | null;
  /** Several diagrams behind one question (front and rear door panels): tabs
   *  in place of the breadcrumb, to move between them without leaving. */
  sectionTabs?: { key: string; label: string; count?: number; pending?: boolean }[] | null;
  activeSectionKey?: string | null;
  onSelectSection?: (key: string) => void;
  /** Other sections that may hold the part. Fetched lazily — `onOpenCandidates`
   *  fires when the menu opens, `null` means "not loaded yet". An empty array
   *  is a real answer ("this part lives in one section"), not a failure. */
  candidateSections?: DiagramCandidateSection[] | null;
  onOpenCandidates?: () => void;
  onPickCandidate?: (section: DiagramCandidateSection) => void;
  /** "Search the whole catalog for …" at the foot of that menu. */
  onSearchCatalog?: () => void;
  /** The caller's header (the question block). `ctx` lets an answer option
   *  drive the list's filters: its parts only, on the list and the drawing. */
  renderHeader: (ctx?: DiagramHeaderCtx) => React.ReactNode;
  renderFooter: (ctx: { close: () => void }) => React.ReactNode;
  /** Floats over the diagram pane (the AI comparison bubble). */
  renderOverlay?: () => React.ReactNode;
  /** Under a label's name in the list (an AI note about that candidate). */
  renderBlockNote?: (row: DiagramRow) => React.ReactNode;
  /** Extra items on a part row's quiet description line. */
  rowBits?: (row: DiagramRow, nested: boolean) => React.ReactNode[];
  /** What tells THIS part number apart (the AI comparison's tags + its
   *  "choose this when" line), pinned to EVERY part row — single or nested —
   *  right under the number, so the comparison lives in the list itself. */
  renderRowInsight?: (row: DiagramRow, ctx: { shownValues: string[]; names: string[] }) => React.ReactNode;
  /** True when something outside the catalog (the AI comparison) sets this row
   *  apart. Used to tell the user when equally-matched rows look identical. */
  rowHasDifference?: (row: DiagramRow, ctx: { names: string[] }) => boolean;
  /** A strip at the top of the parts list (the AI comparison summary). */
  renderListIntro?: () => React.ReactNode;
  /** Trailing control on a part row (Add to cart / In order). `primary` is true
   *  for ONE row — the selected one, else the best match — so only it wears the
   *  filled action; the rest go ghost (Match Strength Bars spec §3). */
  renderRowAction?: (row: DiagramRow, ctx: { primary: boolean }) => React.ReactNode;
  /** What the customer asked for ("radiator hose"). Drives the functionality
   *  tag: green on every part of that kind, light red on the rest. */
  requestedType?: string | null;
  /** The numbered answer option a row satisfies ("① Base / bracket only"). */
  rowAnswer?: (row: DiagramRow) => { n: number; label: string } | null;
  /** The AI comparison's distinguishing values for a row — shown as feature /
   *  position tags in the row's one tag row (deduplicated there). */
  rowAiTags?: (row: DiagramRow, ctx: { shownValues: string[]; names: string[] }) => AiRowTags | null;
  /** What the AI comparison says separates the candidates ("side") — named
   *  in the diff hint on equally high parts. */
  diffDimension?: string | null;
  /** The diff hint's "compare" opens the side-by-side comparison. */
  onCompare?: () => void;
  /** "What's different?" at the top of the parts list: compares the
   *  ambiguous candidates and names what they share. `loading` while the
   *  comparison is being written. */
  whatsDifferent?: { onOpen: () => void; loading?: boolean } | null;
  /** Parts picked for this question across every diagram (the count the
   *  footer and Confirm button show). Defaults to the rows selected here. */
  selectedCount?: number;
  /** Drop every pick for the question. Shown as "Clear selection" at the top
   *  of the list while anything is selected. */
  onClearSelection?: () => void;
  /** Under the list's empty state ("No parts match…"): the way out when the
   *  part isn't here (the question's "Can't find it?"). */
  renderEmptyAction?: () => React.ReactNode;
  /** Make the AI-generated 3D rendering of a drawing (the "3D · AI generated"
   *  view beside "2D · Original EPC"). Resolves to the rendering's URL. Without
   *  it the viewer shows the 2D drawing only. */
  render3d?: ((url: string, signal: AbortSignal) => Promise<string>) | null;
  /** "Model · Interactive 3D": builds a 3D model of the drawing (one mesh per
   *  part number) and resolves to a viewer URL shown in an iframe. Given only
   *  when the org's `diagram_3d_models` flag is on. */
  build3dModel?: ((url: string, parts: Array<{ pn: string; name: string | null; alt: string[] }>, signal: AbortSignal, rebuild?: boolean, onProgress?: (p: { progress: number; stage: string; detail: string | null }) => void) => Promise<string>) | null;
  /** Turn on answer `n`'s filter once the diagram `sectionKey` is open (an
   *  answer picked while another diagram was showing). `nonce` per request. */
  answerRequest?: { n: number; label: string; sectionKey: string; nonce: number } | null;
}

const PRIMARY = '#6866ff';
const TAG_BOX = 44;
const NULLISH = ['n/a', 'na', 'none', 'null', '-', '—'];
const OE_NOTE_KEYS = new Set(['remark', 'remarks', 'note', 'notes', 'optionalpack', 'optionalpackage']);
const isNoteKey = (k: string) => OE_NOTE_KEYS.has(String(k).toLowerCase().replace(/[\s_-]/g, ''));
/** Catalog fields that live under "Catalog details" only, never on the row:
 *  supersession / replacement chains and logistics, alternatives, packaging
 *  and packed sizes. */
const isDetailsOnlyKey = (k: string): boolean => {
  const n = String(k).toLowerCase().replace(/[^a-z0-9]/g, '');
  return isSupersessionDetailKey(k) || n.includes('alternat') || n.startsWith('optionalpart') || n.includes('pack');
};
/** Every number the catalog's own supersession fields name ("Replaced by",
 *  "Replaces"…), one per entry — what a search by a replacement number reads
 *  besides the backend's normalised chain. */
const supersessionValues = (r: DiagramRow): string[] =>
  Object.entries(r.extra_details || {})
    .filter(([k]) => isSupersessionDetailKey(k))
    .flatMap(([, v]) => String(v ?? '').split(/[,;+→|/]/))
    .map(v => v.trim()).filter(Boolean);
/** The row carries supersession data (the SS badge). Logistics notes share
 *  the details section but are not a supersession. */
const hasSupersessionData = (r: DiagramRow): boolean =>
  Object.entries(r.extra_details || {}).some(([k, v]) => isSupersessionDetailKey(k)
    && !String(k).toLowerCase().replace(/[^a-z0-9]/g, '').startsWith('logistic')
    && !!String(v ?? '').trim() && !NULLISH.includes(String(v).trim().toLowerCase()))
  || supersessionNumbers(r).length > 0;

/** Every raw catalog string the viewer's list shows for these rows — the
 *  strings it translates before opening. Exported so a caller can prefetch
 *  the translation while the drawing loads (the viewer then opens at once). */
export const diagramTexts = (rows: DiagramRow[]): string[] => {
  const texts = new Set<string>();
  for (const r of rows) {
    if (r.name) texts.add(r.name.trim());
    if (r.description) texts.add(r.description.trim());
    for (const [k, v] of Object.entries(r.extra_details || {})) {
      if (!isNoteKey(k)) continue;
      const s = String(v ?? '').trim();
      if (s) texts.add(s);
    }
    for (const s of positionChipsFromExtra(r.extra_details)) texts.add(s);
    for (const d of catalogDetailEntries(r.extra_details)) texts.add(d.value);
  }
  return Array.from(texts).filter(Boolean);
};
const normPartKey = (v: unknown): string => String(v ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

// Words too generic (or already covered by the side/position evidence) to count
// as a name match.
const EVIDENCE_STOPWORDS = new Set([
  'para', 'com', 'dos', 'das', 'the', 'and', 'for', 'with', 'del', 'los', 'las', 'con',
  'lado', 'left', 'right', 'front', 'rear', 'izquierdo', 'izquierda', 'derecho', 'derecha',
  'esquerdo', 'esquerda', 'direito', 'direita', 'delantero', 'delantera', 'trasero', 'trasera',
  'dianteiro', 'traseiro', 'parte',
]);
const evidenceTokens = (text: string): Set<string> => new Set(
  text.toLowerCase().replace(/[^a-z0-9À-ɏ]+/g, ' ').split(' ')
    .filter(w => w.length >= 4 && !EVIDENCE_STOPWORDS.has(w)),
);
const POSITION_LABEL: Record<string, [string, string]> = {
  left: ['chat.clarification.posLeft', 'Left'],
  right: ['chat.clarification.posRight', 'Right'],
  front: ['chat.clarification.posFront', 'Front'],
  rear: ['chat.clarification.posRear', 'Rear'],
};

type Group = { key: string; rows: DiagramRow[]; top: DiagramRow; duplicateCount: number };
type LabelBlock = { name: string; groups: Group[]; score: number | null; top: Group };

const DiagramModal: React.FC<DiagramModalProps> = ({
  open, t, diagram, rows, onClose, onToggleRow, selectionMode = 'single', disabled,
  otherPartsOpen = true, requestTexts, preTranslations, legendExtras, focusRequest,
  sectionName, groupName, sectionCode, sectionTabs, activeSectionKey, onSelectSection, candidateSections, onOpenCandidates, onPickCandidate, onSearchCatalog,
  renderHeader, renderFooter, renderOverlay, renderBlockNote, rowBits, renderRowInsight, renderListIntro, rowHasDifference, renderRowAction,
  selectedCount: selectedCountProp, onClearSelection, renderEmptyAction, render3d, build3dModel,
  requestedType, rowAnswer, rowAiTags, diffDimension, onCompare, whatsDifferent, answerRequest,
}) => {
  const { t: hookT, i18n } = useTranslation();
  const { getActiveOrderId } = useOrder();
  const translate = t ?? (hookT as ((key: string, opts?: any) => string) | undefined);
  // i18next interpolates {{vars}} itself; the no-translator fallback does it by hand.
  const tr = (key: string, def: string, vars?: Record<string, unknown>) => (
    translate
      ? String(translate(key, { defaultValue: def, ...(vars || {}) }))
      : Object.entries(vars || {}).reduce((acc, [k, v]) => acc.split(`{{${k}}}`).join(String(v)), def)
  );

  const [zoom, setZoom] = useState(1);
  const [naturalDims, setNaturalDims] = useState<{ w: number; h: number } | null>(null);
  const [active, setActive] = useState<string | null>(null); // row key
  const [flashRow, setFlashRow] = useState<string | null>(null);
  // List order: by match (default) or the catalog's own reference order.
  const [sortMode, setSortMode] = useState<'match' | 'ref'>('match');
  // On a phone the drawing and the list cannot share one screen — the list wins
  // and the diagram becomes a useless letterbox. Start folded there; the list
  // button in the toolbar brings it back.
  const [showPartsTable, setShowPartsTable] = useState(
    () => !(typeof window !== 'undefined' && window.innerWidth <= 600),
  );
  const [showOther, setShowOther] = useState(otherPartsOpen);
  const [partSearch, setPartSearch] = useState('');
  // Tag filters (ids from filterId), AND-ed. A new drawing starts unfiltered.
  const [activeFilters, setActiveFilters] = useState<Set<string>>(() => new Set());
  useEffect(() => { setActiveFilters(new Set()); }, [activeSectionKey, diagram?.diagram_url]);
  const toggleFilter = (id: string) => setActiveFilters(prev => toggleFilterId(prev, id));
  const [translations, setTranslations] = useState<Record<string, string>>({});
  // Tag / chip texts whose background translation has settled (see the tag batch).
  const [tagTried, setTagTried] = useState<Set<string>>(() => new Set());
  const tx = (text?: string | null) => (text && (translations[text] || preTranslations?.[text])) || (text || '');

  const diagramUrl = diagram?.diagram_url ?? null;
  // The drawing's natural size decides where every tag and mask lands, so read
  // it off the ELEMENT rather than trusting we caught its load event: a cached
  // image (the inline preview already fetched it) fires `load` before this
  // effect runs, and blindly resetting to null there left the drawing with no
  // tags and no masks at all. `complete` is false until the NEW src has loaded,
  // so this never reports the previous drawing's size.
  const imgRef = useRef<HTMLImageElement | null>(null);
  const syncNaturalDims = () => {
    const img = imgRef.current;
    const next = img?.complete && img.naturalWidth > 0 ? { w: img.naturalWidth, h: img.naturalHeight } : null;
    setNaturalDims(prev => (prev && next && prev.w === next.w && prev.h === next.h) || (!prev && !next) ? prev : next);
  };
  // Also read it when the image ELEMENT appears: the viewer may mount the
  // drawing only after its loading gate lifts (first open waits for the
  // translation), long after the effect below ran with no element.
  const imgCallbackRef = React.useCallback((el: HTMLImageElement | null) => {
    imgRef.current = el;
    if (el) syncNaturalDims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setZoom(1); setActive(null); syncNaturalDims(); }, [diagramUrl, open]);

  // ── Drag-to-pan (scrolls the surface); a real drag swallows the click after it.
  const pan = useRef({ active: false, x: 0, y: 0, left: 0, top: 0, moved: false });
  const suppressClick = useRef(false);
  const onPanDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.style.cursor = 'grabbing';
    pan.current = { active: true, x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: false };
  };
  const onPanMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pan.current.active) return;
    const dx = e.clientX - pan.current.x;
    const dy = e.clientY - pan.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) pan.current.moved = true;
    e.currentTarget.scrollLeft = pan.current.left - dx;
    e.currentTarget.scrollTop = pan.current.top - dy;
  };
  const onPanEnd = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.cursor = 'grab';
    if (pan.current.moved) {
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 0);
    }
    pan.current.active = false;
  };

  // ── Portal host owned by this component (avoids React's "removeChild: node is
  // not a child" when <body> is mutated by other portals/toasts/auto-translate).
  const portalHostRef = useRef<HTMLDivElement | null>(null);
  if (portalHostRef.current === null && typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.setAttribute('translate', 'no');
    el.className = 'notranslate';
    portalHostRef.current = el;
  }
  useEffect(() => {
    const host = portalHostRef.current;
    if (!host) return;
    document.body.appendChild(host);
    return () => { host.remove(); };
  }, []);

  // Lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Measure the surface so the image can be CONTAIN-fitted in px (pure-CSS
  // contain fights the flex layout's indefinite % heights).
  // A CALLBACK ref, not an effect on `open`: the surface mounts only after the
  // viewer's loading gate (image + translation) lifts, which is after `open`
  // flipped — the effect then found no element, never measured, and the
  // drawing fell back to 100% width: not fitted, overflowing, and zoom-out
  // disabled at "100%" (live 2026-09-28).
  const [surfaceSize, setSurfaceSize] = useState<{ w: number; h: number } | null>(null);
  const surfaceObserver = useRef<ResizeObserver | null>(null);
  const surfaceEl = useRef<HTMLDivElement | null>(null);
  const surfaceRef = React.useCallback((el: HTMLDivElement | null) => {
    surfaceObserver.current?.disconnect();
    surfaceObserver.current = null;
    surfaceEl.current = el;
    if (!el) { setSurfaceSize(null); return; }
    const update = () => setSurfaceSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    if (typeof ResizeObserver !== 'undefined') {
      surfaceObserver.current = new ResizeObserver(update);
      surfaceObserver.current.observe(el);
    }
  }, []);
  useEffect(() => () => surfaceObserver.current?.disconnect(), []);

  // ── Rows → list groups ─────────────────────────────────────────────────────
  // A callout with no part for this vehicle (the drawing serves every
  // variant; the catalog lists this VIN's rows only) is not a list entry —
  // its tag is drawn crossed out, and the refs are named once under the list.
  const rowHasPart = (r: DiagramRow) => r.candidate || !!r.name || !!r.partNumber;
  const listRows = useMemo(() => rows.filter(r => !r.prior && rowHasPart(r)), [rows]);
  const unavailableRefs = useMemo(() => {
    const withPart = new Set(rows.filter(rowHasPart).map(r => String(r.callout)));
    const refs = Array.from(new Set(rows.filter(r => !rowHasPart(r)).map(r => String(r.callout))))
      .filter(c => c && !withPart.has(c));
    return refs.sort((a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0) || a.localeCompare(b));
  }, [rows]);
  // ── Sheets ────────────────────────────────────────────────────────────────
  // A section's drawings. Older payloads carry one image, so synthesise a
  // single sheet from them and the rest of the viewer stays sheet-agnostic.
  const sheets: DiagramSheet[] = useMemo(() => sheetsFrom(diagram), [diagram]);

  const [sheetIdx, setSheetIdx] = useState<number>(1);
  const sheet = useMemo(
    () => sheets.find(sh => sh.index === sheetIdx) || sheets[0] || null,
    [sheets, sheetIdx],
  );
  const multiSheet = sheets.length > 1;

  /** Which sheet a callout is drawn on (first sheet that carries it). */
  const sheetOfCallout = useMemo(() => sheetOfCalloutMap(sheets), [sheets]);

  const sheetOfRow = (r: DiagramRow): number =>
    sheetOfRowFn(r, sheetOfCallout, diagram?.primary_sheet ?? sheets[0]?.index ?? 1);

  // Open on the sheet holding the best candidate, not on sheet 1 — on the
  // reference case (32/335) the arms being compared are all on sheet 2, so
  // defaulting to the first sheet opens on a drawing with none of the answers.
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !sheets.length) return;
    const stamp = `${diagram?.sheets?.length ?? 0}:${sheets.map(sh => sh.id ?? sh.index).join(',')}`;
    if (openedFor.current === stamp) return;
    openedFor.current = stamp;
    setSheetIdx(openingSheet(sheets, rows, diagram?.primary_sheet ?? null));
  }, [open, sheets, rows, diagram]);

  const goSheet = (next: number, focusCallout?: string) => {
    if (!sheets.length) return;
    const clamped = Math.max(sheets[0].index, Math.min(sheets[sheets.length - 1].index, next));
    setSheetIdx(clamped);
    if (focusCallout) setActive(null);
  };

  // The category row says "Category 1 of N": ask the resolver for the other
  // categories that may hold the part as soon as the viewer opens (in the
  // background; the row spins until they land).
  // Only when a category can actually be opened from here (onPickCandidate).
  useEffect(() => {
    if (open && candidateSections == null && onPickCandidate) onOpenCandidates?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeSectionKey, candidateSections == null]);

  // A non-candidate the scorer rated ABOVE the weakest candidate is a likelier
  // answer than a part we already list on top — it must not sit hidden under
  // "Other parts". It joins the top section, in score order, so that section
  // is always the best-scored rows (one descending order, no inversions).
  const promotedKeys = useMemo(() => {
    const scores = listRows.filter(r => r.candidate && typeof r.score === 'number').map(r => r.score as number);
    if (!scores.length) return new Set<string>();
    const floor = Math.min(...scores);
    return new Set(listRows.filter(r => !r.candidate && typeof r.score === 'number' && (r.score as number) > floor).map(r => r.key));
  }, [listRows]);
  const isPrimaryRow = (r: DiagramRow) => r.candidate || promotedKeys.has(r.key);

  const groups: Group[] = useMemo(() => {
    // Candidates (and better-scored promoted parts) first, then by estimated
    // match (best first); equal estimates keep catalog order so the list is
    // still walkable by ref.
    // "Catalog order" keeps the candidate split but walks each part by ref.
    // No estimate sorts last (-1); ties fall back to ref order (stable).
    const sorted = [...listRows].sort((a, b) => {
      const pa = isPrimaryRow(a), pb = isPrimaryRow(b);
      if (pa !== pb) return pa ? -1 : 1;
      if (sortMode === 'match') {
        const sa = typeof a.score === 'number' ? a.score : -1;
        const sb = typeof b.score === 'number' ? b.score : -1;
        if (sa !== sb) return sb - sa;
      }
      return (parseInt(a.callout, 10) || 0) - (parseInt(b.callout, 10) || 0);
    });
    // One OE part repeated as several EPC records under the same callout is
    // provenance, not two choices: collapse when OE number + callout + raw name
    // + finish + positions all agree. Prefer the selected copy.
    const deduped: Array<{ row: DiagramRow; duplicateCount: number }> = [];
    const seenDup = new Map<string, number>();
    for (const row of sorted) {
      const name = (row.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const finish = catalogDetailEntries(row.extra_details).find(d => normalizeDetailKey(d.key) === 'finish')?.value.toLowerCase().replace(/\s+/g, ' ').trim() || '';
      const positions = positionChipsFromExtra(row.extra_details).map(v => v.toLowerCase()).sort().join('|');
      const pn = normPartKey(row.partNumber);
      const dupKey = pn && row.callout && name ? `${pn}::${row.callout}::${name}::${finish}::${positions}` : '';
      const at = dupKey ? seenDup.get(dupKey) : undefined;
      if (at == null) {
        if (dupKey) seenDup.set(dupKey, deduped.length);
        deduped.push({ row, duplicateCount: 1 });
        continue;
      }
      deduped[at].duplicateCount += 1;
      if (row.selected && !deduped[at].row.selected) deduped[at].row = row;
    }
    // A bundle (same part at several callouts) is one list entry.
    const out: Group[] = [];
    const byBundle = new Map<string, number>();
    for (const { row, duplicateCount } of deduped) {
      const bk = row.bundleKey || row.key;
      const idx = byBundle.get(bk);
      if (idx != null) { out[idx].rows.push(row); continue; }
      byBundle.set(bk, out.length);
      out.push({ key: bk, rows: [row], top: row, duplicateCount });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRows, sortMode, promotedKeys]);

  // Catalog kits (a kit SKU followed by its component SKUs, in catalog order).
  const kits = useMemo(() => {
    const groupOf = (row: DiagramRow) => groups.find(g => g.rows.some(r => catalogPartIdentity(r) === catalogPartIdentity(row)));
    return catalogKits(listRows).map(({ kit, members }) => ({ kit: groupOf(kit), members: members.map(groupOf).filter((g): g is Group => !!g) }))
      .filter((k): k is { kit: Group; members: Group[] } => !!k.kit);
  }, [listRows, groups]);
  // One list section per KIT LABEL: the same callout + name sold as several
  // kit SKUs (Xentry lists every version, then the components once, after the
  // last one) is one choice between versions — not N identical "choose the kit"
  // boxes, one of them seemingly empty. Components show once, under the
  // versions. Conflicts (kit vs. its own components) still use `kits`.
  type KitCluster = { kits: Group[]; members: Group[] };
  const kitClusters = useMemo(() => {
    const labelOf = (g: Group) => `${(g.top.name || '').toLowerCase().replace(/\s+/g, ' ').trim()}::${g.top.callout}`;
    const out: KitCluster[] = [];
    const byLabel = new Map<string, KitCluster>();
    const placed = new Set<Group>();
    for (const k of kits) {
      let c = byLabel.get(labelOf(k.kit));
      if (!c) { c = { kits: [], members: [] }; byLabel.set(labelOf(k.kit), c); out.push(c); }
      if (!placed.has(k.kit)) { placed.add(k.kit); c.kits.push(k.kit); }
    }
    for (const k of kits) {
      const c = byLabel.get(labelOf(k.kit))!;
      for (const m of k.members) if (!placed.has(m)) { placed.add(m); c.members.push(m); }
    }
    return out;
  }, [kits]);
  const kitGroups = useMemo(() => new Set(kitClusters.flatMap(c => [...c.kits, ...c.members])), [kitClusters]);
  const kitConflicts = (g: Group): Group[] => {
    const family = kits.find(k => k.kit === g || k.members.includes(g));
    return family ? (family.kit === g ? family.members : [family.kit]) : [];
  };

  // Same-named CANDIDATES: the catalog fields whose values differ between them
  // (finish, equipment…) are promoted onto each row so the choice is visible
  // without opening anything. A field only some rows state is not a choice.
  const variantDifferences = useMemo(() => {
    const entriesOf = (ed: Record<string, string> | null | undefined): CatalogDetail[] => {
      const out = catalogDetailEntries(ed);
      for (const [k, v] of Object.entries(ed || {})) {
        if (!OE_NOTE_KEYS.has(normalizeDetailKey(k))) continue;
        const value = String(v ?? '').trim();
        if (!value || NULLISH.includes(value.toLowerCase())) continue;
        out.push({ key: k, label: humanizeDetailKey(k), value });
      }
      return out;
    };
    const byName = new Map<string, Group[]>();
    for (const g of groups) {
      if (!g.top.candidate) continue;
      const name = (g.top.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!name) continue;
      byName.set(name, [...(byName.get(name) || []), g]);
    }
    const out = new Map<Group, CatalogDetail[]>();
    for (const family of Array.from(byName.values())) {
      if (family.length < 2) continue;
      const entries = family.map(g => entriesOf(g.top.extra_details));
      const keys = new Set<string>();
      // Replaces / supersession chain / logistics notes, alternatives and
      // packaging differ between ANY two numbers and say nothing about which
      // one fits — Catalog details only.
      for (const list of entries) for (const d of list) if (!isDetailsOnlyKey(d.key)) keys.add(normalizeDetailKey(d.key));
      const differing = new Set<string>();
      const pns = family.map(g => normPartKey(g.top.partNumber));
      for (const norm of Array.from(keys)) {
        const values = entries.map(list => list.find(d => normalizeDetailKey(d.key) === norm)?.value.toLowerCase().replace(/\s+/g, ' ').trim() || '');
        if (!values.every(Boolean) || new Set(values).size < 2) continue;
        // "Optional parts: <the other version>" on each version only points the
        // rows at each other — it does not say how they differ.
        const pointsAtSiblings = values.every((v, i) => {
          const nv = normPartKey(v);
          return pns.some((pn, j) => j !== i && pn.length >= 5 && nv.includes(pn));
        });
        if (!pointsAtSiblings) differing.add(norm);
      }
      if (!differing.size) continue;
      family.forEach((g, i) => {
        const own = entries[i].filter(d => differing.has(normalizeDetailKey(d.key)));
        if (own.length) out.set(g, own);
      });
    }
    return out;
  }, [groups]);

  // ── Translate every raw catalog string the list shows, in one batch, once
  // per language. Keyed by original text → translated.
  useEffect(() => { setTranslations({}); setTagTried(new Set()); }, [i18n.language]);
  // ONLY the catalog strings the caller prefetched (diagramTexts) gate the
  // viewer: one string the prefetch did not cover turns the whole batch into
  // a translation round trip, and the viewer waits for it. Everything else
  // (the requested type, tag and diagram names) goes through the background
  // tag batch below.
  const textKey = useMemo(() => diagramTexts(listRows).join('\x01'), [listRows]);
  // While the catalog strings are being translated the viewer shows its
  // loading state: opening on the originals and then swapping every row to
  // the translation read as the list changing under the customer. Capped, so
  // a slow translation never keeps the diagram closed (originals then stay).
  const [translating, setTranslating] = useState(false);
  // Has the viewer shown its content since it opened, and has the first
  // translation pass settled? (See the shell.)
  const shownOnce = useRef(false);
  const [firstSettled, setFirstSettled] = useState(false);
  useEffect(() => {
    if (!open) { setTranslating(false); setFirstSettled(false); return; }
    if (!textKey) { setTranslating(false); setFirstSettled(true); return; }
    const missing = textKey.split('').filter(s => !translations[s] && !preTranslations?.[s]);
    if (missing.length === 0) { setTranslating(false); setFirstSettled(true); return; }
    const items = missing.map((text, i) => ({ key: String(i), text }));
    let cancelled = false;
    setTranslating(true);
    const cap = window.setTimeout(() => { if (!cancelled) { setTranslating(false); setFirstSettled(true); } }, 6000);
    TranslationBatchManager.request(String(getActiveOrderId() ?? ''), i18n.language || '', items)
      .then(res => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        items.forEach(it => { const v = res[it.key]; if (v) map[it.text] = v; });
        if (Object.keys(map).length) setTranslations(prev => ({ ...prev, ...map }));
      })
      .catch(() => { /* originals stay */ })
      .finally(() => { if (!cancelled) { window.clearTimeout(cap); setTranslating(false); setFirstSettled(true); } });
    return () => { cancelled = true; window.clearTimeout(cap); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, textKey, i18n.language]);

  // ── Selection + focus ──────────────────────────────────────────────────────
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollToRow = (key: string) => {
    requestAnimationFrame(() => rowRefs.current.get(key)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  };
  const flashTimer = useRef<number | null>(null);
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);
  useEffect(() => {
    if (!focusRequest) return;
    setPartSearch('');
    setActive(focusRequest.key);
    scrollToRow(focusRequest.key);
    setFlashRow(focusRequest.key);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashRow(null), 2200);
  }, [focusRequest]);

  const choose = (g: Group) => {
    if (disabled || suppressClick.current) return;
    setActive(g.top.key);
    // The part may be drawn on another sheet — show it there rather than
    // leaving the user on a drawing where their selection has no callout.
    const target = sheetOfRow(g.top);
    if (target && target !== sheetIdx) goSheet(target);
    const next = !g.top.selected;
    // A kit SKU and its component SKUs are alternatives: never leave the kit
    // selected alongside a component, or vice versa.
    if (next) for (const c of kitConflicts(g)) if (c.top.selected) onToggleRow(c.top, false);
    onToggleRow(g.top, next);
    postModel({ type: 'select', pn: next ? g.top.partNumber : null });
  };

  // ── Tags on the drawing ────────────────────────────────────────────────────
  // The tag for a callout follows the row that best represents it: the selected
  // sibling, else a candidate, else the first catalog part there.
  const rowsAt = (label: string) => rows.filter(r => String(r.callout) === String(label));
  const leadAt = (label: string): DiagramRow | undefined => {
    const at = rowsAt(label);
    return at.find(r => r.selected) ?? at.find(r => r.candidate) ?? at[0];
  };
  const hasInfo = (r: DiagramRow | undefined) => !!r && (r.candidate || !!r.name || !!r.partNumber);
  const groupsAt = (label: string) => groups.filter(g => g.rows.some(r => String(r.callout) === String(label)));
  const onTagClick = (label: string) => {
    if (suppressClick.current) return;
    const gs = groupsAt(label);
    if (!gs.length) return;
    const lead = leadAt(label);
    const target = (lead && gs.find(g => g.rows.some(r => r.key === lead.key))) || gs[0];
    setActive(target.top.key);
    scrollToRow(target.top.key);
    // The badge displays one lead row even when several catalog rows share its
    // callout. Clicking it must toggle that same visible row, not silently do
    // nothing and force the user into the sidebar.
    choose(target);
  };

  const boxFracs = (c: DiagramCalloutBox) => {
    let { x: fx, y: fy, w: fw, h: fh } = c.bounds;
    if (!c.normalized) {
      if (!naturalDims) return null;
      fx /= naturalDims.w; fy /= naturalDims.h; fw /= naturalDims.w; fh /= naturalDims.h;
    }
    return { left: `${fx * 100}%`, top: `${fy * 100}%`, width: `${fw * 100}%`, height: `${fh * 100}%` };
  };

  const fitScale = (naturalDims && surfaceSize && surfaceSize.w > 0 && surfaceSize.h > 0)
    ? Math.min(surfaceSize.w / naturalDims.w, surfaceSize.h / naturalDims.h)
    : null;
  const renderScale = fitScale ? fitScale * zoom : null;
  // Ref tags are drawn at a fixed pixel size, which swallows the drawing when
  // the surface is a phone-width strip. Shrink them with the surface (never
  // below half, or the numbers stop being readable).
  const tagViewScale = surfaceSize && surfaceSize.w > 0 ? Math.min(1, Math.max(0.5, surfaceSize.w / 620)) : 1;
  // Keep ref tags from covering each other: resolve collisions in RENDERED
  // pixels and offset the loser by a transform (leader line back to its part).
  const tagOffsets = useMemo(() => {
    const out = new Map<number, { dx: number; dy: number; s: number }>();
    const callouts = diagram?.callouts || [];
    if (!renderScale || !naturalDims || callouts.length < 2) return out;
    const rank = (p: CalloutPriority) => (p === 'hero' ? 0 : p === 'secondary' ? 1 : p === 'optional' ? 2 : 3);
    const entries = callouts
      .map((c, i) => ({ i, c, priority: leadAt(String(c.label))?.priority ?? 'na' as CalloutPriority }))
      .filter(e => !!e.c.bounds)
      .sort((a, b) => rank(a.priority) - rank(b.priority));
    const anchors = entries.map(e => {
      const b = e.c.bounds;
      const n = e.c.normalized;
      return {
        label: String(e.c.label),
        cx: (n ? (b.x + b.w / 2) * naturalDims.w : b.x + b.w / 2) * renderScale,
        cy: (n ? (b.y + b.h / 2) * naturalDims.h : b.y + b.h / 2) * renderScale,
        priority: e.priority,
        boxW: (n ? b.w * naturalDims.w : b.w) * renderScale,
        boxH: (n ? b.h * naturalDims.h : b.h) * renderScale,
      };
    });
    if (!anchors.every(a => Number.isFinite(a.cx) && Number.isFinite(a.cy))) return out;
    resolveTagOverlaps(anchors, tagViewScale).forEach((pos, n) => {
      const dx = pos.cx - anchors[n].cx;
      const dy = pos.cy - anchors[n].cy;
      if (dx || dy || pos.scale !== 1) out.set(entries[n].i, { dx, dy, s: pos.scale });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagram, rows, renderScale, naturalDims, tagViewScale]);

  // Circular ref tag (spec §3/§4) as inline SVG — same metrics as the canvas
  // tag the cart thumbnail draws.
  //
  // Two orthogonal signals, so a drawing can be read at a glance:
  //   • the ANSWER is navy — the user's pick (or the result's recommended
  //     part). One tag, unmistakable, whatever its score.
  //   • every other SCORED part wears its match tier (matchTagStyle) — High
  //     filled purple, Medium amber wash, Low grey outline, Very low dashed —
  //     the same colours as the list's strength bars, so the ranking the list
  //     states is visible on the map. Parts the search never
  //     scored keep the plain ramp — "we didn't weigh this" is not "we weighed
  //     it and it's weak" — and a part with no catalog info stays dashed +
  //     slashed.
  // Selection adds a lilac halo; "selected earlier" a navy ✓ badge; in cart a
  // green ✓ badge.
  const renderCalloutTag = (priority: CalloutPriority, o: { number: string; score?: number | null; selected?: boolean; prior?: boolean; inCart?: boolean; active?: boolean; offset?: { dx: number; dy: number; s?: number } }) => {
    const na = priority === 'na';
    const c = TAG_BOX / 2;
    const off = o.offset && (o.offset.dx || o.offset.dy) ? o.offset : undefined;
    const tagScale = o.offset?.s ?? tagViewScale;
    const { r: R, halfW } = diagramTagMetrics(o.number, priority);
    const w = halfW * 2;
    const sec = priority === 'secondary';
    // Scored, non-answer parts wear their match tier (spec §4): High filled
    // purple · Medium amber wash · Low grey outline · Very low dashed.
    const level = matchLevel(o.score);
    const est = !na && priority !== 'hero' && level != null;
    const estStyle = level != null ? matchTagStyle(level) : null;
    // The bar glyph rides on the tag only when it's hovered/focused/selected,
    // so a 30-tag drawing stays readable.
    const showGlyph = level != null && !na && (o.active || o.selected);
    const fg = est && estStyle ? estStyle.fg
      : priority === 'hero' ? '#fff' : na ? DIAGRAM_TAG.naFg : sec ? DIAGRAM_TAG.secFg : DIAGRAM_TAG.optFg;
    const line = est && estStyle ? estStyle.stroke
      : priority === 'hero' ? DIAGRAM_TAG.heroBg : sec ? DIAGRAM_TAG.secStroke : DIAGRAM_TAG.optStroke;
    const pill = (fill: string, extra?: { stroke?: string; strokeWidth?: number; dash?: string }) => (
      <rect x={c - halfW} y={c - R} width={w} height={R * 2} rx={R} ry={R} fill={fill} stroke={extra?.stroke} strokeWidth={extra?.strokeWidth} strokeDasharray={extra?.dash} />
    );
    const badge = (color: string) => (
      <g>
        <circle cx={c + halfW - 3} cy={c - R + 3} r={9.5} fill="#fff" />
        <circle cx={c + halfW - 3} cy={c - R + 3} r={8} fill={color} />
        <path d={`M ${c + halfW - 6.3} ${c - R + 3} l 2.3 2.4 l 4.2 -4.8`} stroke="#fff" strokeWidth={1.8} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
    return (
      <svg
        width={TAG_BOX} height={TAG_BOX} viewBox={`0 0 ${TAG_BOX} ${TAG_BOX}`}
        style={{
          pointerEvents: 'auto', overflow: 'visible', flexShrink: 0,
          ...(tagScale !== 1 ? { transform: `scale(${tagScale})`, transformOrigin: 'center' } : {}),
        }}
        aria-hidden="true"
      >
        {off && (
          <g opacity={0.55}>
            <line x1={c} y1={c} x2={c - off.dx / tagScale} y2={c - off.dy / tagScale} stroke={line} strokeWidth={1} strokeDasharray="3 2" strokeLinecap="round" />
            <circle cx={c - off.dx / tagScale} cy={c - off.dy / tagScale} r={1.8} fill={line} />
          </g>
        )}
        {o.selected && (
          <rect x={c - halfW - 7} y={c - R - 7} width={w + 14} height={R * 2 + 14} rx={R + 7} ry={R + 7} fill={DIAGRAM_TAG.haloFill} stroke={DIAGRAM_TAG.haloStroke} strokeWidth={2} />
        )}
        <g>
          {priority === 'hero'
            ? pill(DIAGRAM_TAG.heroBg)
            : est && estStyle
              ? pill(estStyle.fill, { stroke: estStyle.stroke, strokeWidth: estStyle.strokeWidth, dash: estStyle.dash })
              : na
                ? pill(DIAGRAM_TAG.naFill, { stroke: DIAGRAM_TAG.naStroke, strokeWidth: 1, dash: '3 2' })
                : pill('#fff', { stroke: sec ? DIAGRAM_TAG.secStroke : DIAGRAM_TAG.optStroke, strokeWidth: sec ? 2 : 1.5 })}
          <text x={c} y={c} fill={fg} fontSize={12} fontWeight={700} textAnchor="middle" dominantBaseline="central" fontFamily="Inter Tight, system-ui, -apple-system, sans-serif">{o.number}</text>
          {na && <line x1={c - halfW + 4} y1={c + R - 4} x2={c + halfW - 4} y2={c - R + 4} stroke={DIAGRAM_TAG.naFg} strokeWidth={1.4} strokeLinecap="round" />}
        </g>
        {o.inCart ? badge(DIAGRAM_TAG.cart) : (o.prior && !o.selected) ? badge(DIAGRAM_PALETTE.navy) : null}
        {showGlyph && level != null && (() => {
          // SM glyph (2px bars, 4/6/8/10) on a white chip under the tag.
          const gx = c - 7.5;
          const gy = c + R + (o.selected ? 9 : 3);
          const tint = matchTagStyle(level);
          const barColor = level === 4 ? tint.fill : tint.stroke;
          return (
            <g data-testid="tag-match-glyph">
              <rect x={gx - 3} y={gy - 1} width={21} height={14} rx={4} fill="#fff" stroke={DIAGRAM_PALETTE.border} strokeWidth={0.75} />
              {[4, 6, 8, 10].map((h, i) => (
                <rect key={i} x={gx + i * 3.5} y={gy + 11 - h} width={2} height={h} rx={0.5} fill={i < level ? barColor : '#E4E2EA'} />
              ))}
            </g>
          );
        })()}
      </svg>
    );
  };

  // ── 2D / 3D ──────────────────────────────────────────────────────────────
  // The 3D view is an AI re-rendering of the same drawing at the same aspect
  // ratio (see catax_ai/bl/diagram_3d.py), so it sits in the 2D drawing's
  // frame under the same clickable tags. It is made on first ask, per drawing
  // (each illustration has its own); meanwhile the 2D drawing stays live.
  const [view3d, setView3d] = useState(false);
  const [renders, setRenders] = useState<Record<string, { status: 'loading' | 'ready' | 'error'; url?: string }>>({});
  const renderAborts = useRef<Record<string, AbortController>>({});
  const drawingUrl = (sheet ? sheet.diagram_url : diagram?.diagram_url) ?? null;
  const render3dOf = (url: string | null | undefined) => (url ? renders[url] : undefined);
  const showing3d = view3d && render3dOf(drawingUrl)?.status === 'ready';
  const start3d = (url: string, retry = false) => {
    if (!render3d) return;
    const st = renders[url]?.status;
    if (st === 'loading' || st === 'ready' || (st === 'error' && !retry)) return;
    const ac = new AbortController();
    renderAborts.current[url] = ac;
    setRenders(p => ({ ...p, [url]: { status: 'loading' } }));
    render3d(url, ac.signal)
      .then(u => setRenders(p => ({ ...p, [url]: { status: 'ready', url: u } })))
      .catch(() => setRenders(p => {
        const next = { ...p };
        if (ac.signal.aborted) delete next[url]; else next[url] = { status: 'error' };
        return next;
      }))
      .finally(() => { if (renderAborts.current[url] === ac) delete renderAborts.current[url]; });
  };
  const cancel3d = () => {
    if (drawingUrl) renderAborts.current[drawingUrl]?.abort();
    setView3d(false);
  };
  useEffect(() => {
    if (view3d && drawingUrl) start3d(drawingUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view3d, drawingUrl]);
  useEffect(() => {
    if (open) return;
    setView3d(false);
    for (const ac of Object.values(renderAborts.current)) ac.abort();
  }, [open]);
  useEffect(() => () => { for (const ac of Object.values(renderAborts.current)) ac.abort(); }, []);

  // ── Model (interactive 3D, catax_3d) ─────────────────────────────────────
  // Same shape as the 3D view: built on first ask per drawing, the drawing
  // stays live meanwhile, Cancel stops waiting. Ready = the viewer in an
  // iframe in place of the drawing.
  const [viewModel, setViewModel] = useState(false);
  type ModelProgress = { progress: number; stage: string; detail: string | null };
  const [models, setModels] = useState<Record<string, { status: 'loading' | 'ready' | 'error'; url?: string; progress?: ModelProgress }>>({});
  const modelAborts = useRef<Record<string, AbortController>>({});
  const modelOf = (url: string | null | undefined) => (url ? models[url] : undefined);
  const showingModel = viewModel && modelOf(drawingUrl)?.status === 'ready';
  // The viewer iframe takes zoom and selection from this modal via postMessage
  // (see viewer.html "message" handler): the zoom buttons move its camera and a
  // row click highlights that part number in the model.
  const modelFrame = useRef<HTMLIFrameElement>(null);
  const postModel = (msg: { type: 'zoom'; factor: number } | { type: 'select'; pn: string | null }) =>
    modelFrame.current?.contentWindow?.postMessage({ catax3d: 1, ...msg }, '*');
  const modelZoomSent = useRef(1);
  useEffect(() => {
    if (!showingModel) return;
    const factor = zoom / modelZoomSent.current;
    modelZoomSent.current = zoom;
    if (factor !== 1) postModel({ type: 'zoom', factor });
  }, [zoom, showingModel]);
  // One entry per part number; its callouts ride along as `alt` because many
  // catalogs (Hyundai/Kia) print the callout code on the drawing, not the OE number.
  const modelParts = () => {
    const byPn = new Map<string, { pn: string; name: string | null; alt: string[] }>();
    for (const r of rows) {
      if (!r.partNumber) continue;
      const pn = String(r.partNumber);
      const e = byPn.get(pn) ?? { pn, name: r.name, alt: [] };
      const c = String(r.callout ?? '').trim();
      if (c && !e.alt.includes(c)) e.alt.push(c);
      byPn.set(pn, e);
    }
    return Array.from(byPn.values());
  };
  // retry: after an error. rebuild: throw the finished model away and build it
  // again from scratch (every part regenerated), e.g. after the pipeline improved.
  const startModel = (url: string, retry = false, rebuild = false) => {
    if (!build3dModel) return;
    const st = models[url]?.status;
    if (st === 'loading' || (st === 'ready' && !rebuild) || (st === 'error' && !retry)) return;
    const ac = new AbortController();
    modelAborts.current[url] = ac;
    setModels(p => ({ ...p, [url]: { status: 'loading' } }));
    build3dModel(url, modelParts(), ac.signal, rebuild, (prog) => setModels(p => p[url]?.status === 'loading' ? { ...p, [url]: { status: 'loading', progress: prog } } : p))
      .then(u => setModels(p => ({ ...p, [url]: { status: 'ready', url: u } })))
      .catch(() => setModels(p => {
        const next = { ...p };
        if (ac.signal.aborted) delete next[url]; else next[url] = { status: 'error' };
        return next;
      }))
      .finally(() => { if (modelAborts.current[url] === ac) delete modelAborts.current[url]; });
  };
  const cancelModel = () => {
    if (drawingUrl) modelAborts.current[drawingUrl]?.abort();
    setViewModel(false);
  };
  useEffect(() => {
    if (viewModel && drawingUrl) startModel(drawingUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewModel, drawingUrl]);
  useEffect(() => {
    if (open) return;
    setViewModel(false);
    for (const ac of Object.values(modelAborts.current)) ac.abort();
  }, [open]);
  useEffect(() => () => { for (const ac of Object.values(modelAborts.current)) ac.abort(); }, []);

  const renderViewToggle = () => {
    const st = render3dOf(drawingUrl)?.status;
    const mst = modelOf(drawingUrl)?.status;
    // Compact: "2D" / "3D" with an icon; what each one is ("Original EPC",
    // "AI generated") is the tooltip, not a second word on the button.
    const opt = (on: boolean, icon: React.ReactNode, main: string, sub: string, testId: string, onClick: () => void) => (
      <button type="button" role="radio" aria-checked={on} data-testid={testId} onClick={onClick} title={`${main} · ${sub}`} aria-label={`${main} · ${sub}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', borderRadius: 5, cursor: 'pointer', padding: '3px 8px', fontFamily: 'inherit', fontSize: 11.5, whiteSpace: 'nowrap',
          background: on ? DIAGRAM_PALETTE.bgSurface : 'transparent', boxShadow: on ? '0 1px 3px rgba(20,22,40,0.10)' : 'none',
          color: on ? DIAGRAM_PALETTE.navy : DIAGRAM_PALETTE.textMuted }}>
        {icon}
        <strong style={{ fontWeight: 700 }}>{main}</strong>
      </button>
    );
    return (
      <div role="radiogroup" aria-label={tr('chat.clarification.diagramView', 'Diagram view')} data-testid="diagram-view-toggle"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 7, background: DIAGRAM_PALETTE.bgSubtle, border: `1px solid ${DIAGRAM_PALETTE.border}` }}>
        {opt(!view3d && !viewModel, <Square size={12} aria-hidden />, tr('chat.clarification.view2d', '2D'), tr('chat.clarification.view2dSub', 'Original EPC'), 'view-2d', () => { setView3d(false); setViewModel(false); })}
        {render3d && opt(view3d, st === 'loading' ? <Loader2 size={12} className="animate-spin" aria-hidden /> : <Box size={12} aria-hidden />,
          tr('chat.clarification.view3d', '3D'), tr('chat.clarification.view3dSub', 'AI generated'), 'view-3d',
          () => { setViewModel(false); setView3d(true); if (drawingUrl && st === 'error') start3d(drawingUrl, true); })}
        {build3dModel && opt(viewModel, mst === 'loading' ? <Loader2 size={12} className="animate-spin" aria-hidden /> : <Boxes size={12} aria-hidden />,
          tr('chat.clarification.viewModel', 'Model'), tr('chat.clarification.viewModelSub', 'Interactive 3D'), 'view-model',
          () => { setView3d(false); setViewModel(true); if (drawingUrl && mst === 'error') startModel(drawingUrl, true); })}
      </div>
    );
  };
  // Under the drawing while the 3D view is on its way, or when it failed.
  const render3dStatus = () => {
    const bar: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, border: `1px solid ${DIAGRAM_PALETTE.border}`, background: '#f6f5ff', fontSize: 12.5, flexShrink: 0 };
    const btn: React.CSSProperties = { marginLeft: 'auto', border: `1px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, color: DIAGRAM_PALETTE.textPrimary, flexShrink: 0 };
    if (viewModel && build3dModel) {
      const mst = modelOf(drawingUrl)?.status;
      if (mst === 'loading') return (
        <div data-testid="diagram-model-progress" role="status" style={bar}>
          <Loader2 size={15} className="animate-spin" aria-hidden style={{ color: DIAGRAM_PALETTE.navy, flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>
            <strong style={{ color: DIAGRAM_PALETTE.navy, fontWeight: 700 }}>{tr('chat.clarification.generatingModel', 'Building the 3D model')}</strong>
            {(() => {
              const prog = modelOf(drawingUrl)?.progress;
              const stage = prog && tr(`chat.clarification.modelStage.${prog.stage}`, MODEL_STAGE_TEXT[prog.stage] || prog.stage);
              return prog
                ? <span data-testid="diagram-model-progress-detail" style={{ color: DIAGRAM_PALETTE.textMuted }}> · {Math.round(prog.progress * 100)}% · {stage}{prog.detail ? ` (${prog.detail})` : ''}</span>
                : <span style={{ color: DIAGRAM_PALETTE.textMuted }}> · {tr('chat.clarification.generatingModelSub', 'a few minutes — each part is generated once; the drawing stays live meanwhile')}</span>;
            })()}
          </span>
          <button type="button" data-testid="diagram-model-cancel" onClick={cancelModel} style={btn}>{tr('common.cancel', 'Cancel')}</button>
        </div>
      );
      if (mst === 'error') return (
        <div data-testid="diagram-model-error" role="alert" style={{ ...bar, background: '#fff7ed' }}>
          <span style={{ minWidth: 0, color: DIAGRAM_PALETTE.textPrimary }}>{tr('chat.clarification.failedModel', "Couldn't build the 3D model — the drawing is unchanged.")}</span>
          <button type="button" data-testid="diagram-model-retry" onClick={() => drawingUrl && startModel(drawingUrl, true)} style={btn}>{tr('chat.clarification.tryAgain', 'Try again')}</button>
          <button type="button" aria-label={tr('chat.clarification.close', 'Close')} onClick={() => setViewModel(false)}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, display: 'flex', padding: 2, flexShrink: 0 }}><X size={14} /></button>
        </div>
      );
      return null;
    }
    if (!view3d || !render3d) return null;
    const st = render3dOf(drawingUrl)?.status;
    if (st !== 'loading' && st !== 'error') return null;
    return st === 'loading' ? (
      <div data-testid="diagram-3d-progress" role="status" style={bar}>
        <Loader2 size={15} className="animate-spin" aria-hidden style={{ color: DIAGRAM_PALETTE.navy, flexShrink: 0 }} />
        <span style={{ minWidth: 0 }}>
          <strong style={{ color: DIAGRAM_PALETTE.navy, fontWeight: 700 }}>{tr('chat.clarification.generating3d', 'Generating the 3D view')}</strong>
          <span style={{ color: DIAGRAM_PALETTE.textMuted }}> · {tr('chat.clarification.generating3dSub', 'about 20 s — the 2D drawing stays live while it renders')}</span>
        </span>
        <button type="button" data-testid="diagram-3d-cancel" onClick={cancel3d} style={btn}>{tr('common.cancel', 'Cancel')}</button>
      </div>
    ) : (
      <div data-testid="diagram-3d-error" role="alert" style={{ ...bar, background: '#fff7ed' }}>
        <span style={{ minWidth: 0, color: DIAGRAM_PALETTE.textPrimary }}>{tr('chat.clarification.failed3d', "Couldn't create the 3D view — the 2D drawing is unchanged.")}</span>
        <button type="button" data-testid="diagram-3d-retry" onClick={() => drawingUrl && start3d(drawingUrl, true)} style={btn}>{tr('chat.clarification.tryAgain', 'Try again')}</button>
        <button type="button" aria-label={tr('chat.clarification.close', 'Close')} onClick={() => setView3d(false)}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, display: 'flex', padding: 2, flexShrink: 0 }}><X size={14} /></button>
      </div>
    );
  };

  const renderSurface = (d: { diagram_url: string | null; callouts: DiagramCalloutBox[] }) => {
    const r3 = view3d ? render3dOf(d.diagram_url) : undefined;
    const url3d = r3?.status === 'ready' ? r3.url : undefined;
    return (
    <div
      ref={surfaceRef}
      style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'auto', background: '#fff', borderRadius: 8, cursor: 'grab', userSelect: 'none' }}
      onMouseDown={onPanDown} onMouseMove={onPanMove} onMouseUp={onPanEnd} onMouseLeave={onPanEnd}
    >
      <div style={{
        position: 'relative', lineHeight: 0, margin: 'auto', flexShrink: 0,
        ...(fitScale && naturalDims
          ? { width: Math.round(naturalDims.w * fitScale * zoom), height: Math.round(naturalDims.h * fitScale * zoom) }
          : { width: `${zoom * 100}%` }),
      }}>
        <img
          ref={imgCallbackRef}
          src={d.diagram_url ?? undefined} alt="" draggable={false}
          onLoad={syncNaturalDims}
          style={{ width: '100%', height: fitScale ? '100%' : 'auto', display: 'block' }}
        />
        {/* The 3D rendering covers the drawing exactly: same frame, same
            aspect ratio, so every tag below still sits on its part. */}
        {url3d && (
          <img data-testid="diagram-3d-image" src={url3d} alt={tr('chat.clarification.ai3dTitle', 'AI-generated illustration')} draggable={false}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', display: 'block' }} />
        )}
        {/* Blank the catalog's printed refs, then draw our own tag over each
            (not on the 3D view: a white box would stand out on its shading). */}
        {!url3d && (d.callouts || []).map((c, i) => {
          const fr = c.bounds && boxFracs(c);
          return fr ? <span key={`mask:${i}`} aria-hidden="true" style={{ position: 'absolute', ...fr, background: '#fff', pointerEvents: 'none' }} /> : null;
        })}
        {(d.callouts || []).map((c, i) => {
          if (!c.bounds) return null;
          const fr = boxFracs(c);
          if (!fr) return null;
          const label = String(c.label);
          const lead = leadAt(label);
          const info = hasInfo(lead);
          const at = rowsAt(label);
          const isActive = !!active && at.some(r => r.key === active);
          const interactive = info && !disabled;
          const offset = tagOffsets.get(i);
          return (
            <button
              key={`${label}-${i}`}
              className="diagram-callout-hit"
              data-callout={label}
              type="button"
              disabled={!interactive}
              aria-hidden={!info}
              aria-pressed={!!lead?.selected}
              tabIndex={info ? undefined : -1}
              title={info ? (tx(lead?.name) || `#${label}`) : tr('chat.clarification.notAvailable', 'Not available for this model')}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={interactive ? () => onTagClick(label) : undefined}
              onMouseEnter={interactive ? () => { const g = groupsAt(label)[0]; if (g) setActive(g.top.key); } : undefined}
              data-dimmed={filterActive && !visibleCallouts.has(label) ? 'true' : undefined}
              style={{
                position: 'absolute', ...fr,
                // Left out by the filters / search: faded, still clickable.
                opacity: filterActive && !visibleCallouts.has(label) ? 0.15 : 1,
                transition: 'opacity 0.2s ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', borderRadius: 8,
                background: isActive && info ? 'rgba(30,13,184,0.06)' : 'transparent',
                outline: isActive && info ? '1.5px solid rgba(30,13,184,0.35)' : 'none',
                cursor: info ? (disabled ? 'default' : 'pointer') : 'default',
                pointerEvents: info ? 'auto' : 'none',
                padding: 0, overflow: 'visible',
                ...(offset && (offset.dx || offset.dy)
                  ? { transform: `translate(${offset.dx}px, ${offset.dy}px)` }
                  : {}),
              }}
            >
              {renderCalloutTag(lead?.priority ?? 'na', {
                number: label,
                score: lead?.score ?? null,
                selected: !!lead?.selected,
                prior: !!lead?.prior,
                inCart: at.some(r => r.inCart),
                active: isActive,
                offset,
              })}
            </button>
          );
        })}
      </div>
    </div>
    );
  };

  const renderZoomControls = () => {
    // 100% = the whole drawing fitted to the surface; out to 50%, in to 300%.
    const atMin = zoom <= 0.5;
    const atMax = zoom >= 3;
    const step = (v: number) => setZoom(Math.min(3, Math.max(0.5, Math.round(v * 100) / 100)));
    return (
      <>
        <button type="button" aria-label={tr('chat.clarification.zoomOut', 'Zoom out')} onClick={() => step(zoom - 0.25)} disabled={atMin}
          style={{ border: 'none', background: 'transparent', cursor: atMin ? 'default' : 'pointer', color: atMin ? '#ccc' : '#555', display: 'flex', padding: 3 }}><ZoomOut size={16} /></button>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#666', minWidth: 34, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button type="button" aria-label={tr('chat.clarification.zoomIn', 'Zoom in')} onClick={() => step(zoom + 0.25)} disabled={atMax}
          style={{ border: 'none', background: 'transparent', cursor: atMax ? 'default' : 'pointer', color: atMax ? '#ccc' : '#555', display: 'flex', padding: 3 }}><ZoomIn size={16} /></button>
      </>
    );
  };

  // Legend: what a selected tag looks like, the statuses this surface can show,
  // and the four match-strength glyphs.
  // ── Viewport overlays ─────────────────────────────────────────────────────
  // Breadcrumb, sheet tabs and legend sit INSIDE the drawing area: they
  // describe what is on screen, and in the header strip they competed with the
  // question itself for the reader's first glance.
  const pillBase: React.CSSProperties = {
    background: 'rgba(255,255,255,0.94)',
    border: `1px solid ${DIAGRAM_PALETTE.border}`,
    boxShadow: '0 1px 4px rgba(20,22,40,0.08)',
    borderRadius: 9,
  };

  // Arrow keys page through sheets, unless the user is typing (the parts
  // search sits in the same modal).
  useEffect(() => {
    if (!open || !multiSheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
      e.preventDefault();
      goSheet(sheetIdx + (e.key === 'ArrowRight' ? 1 : -1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, multiSheet, sheetIdx, sheets]);

  const renderViewport = () => showingModel ? (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      <iframe data-testid="diagram-model-viewer" ref={modelFrame} src={`${modelOf(drawingUrl)!.url}${modelOf(drawingUrl)!.url!.includes('?') ? '&' : '?'}embed=1`}
        onLoad={() => { modelZoomSent.current = zoom; const sel = rows.find(r => r.selected && r.partNumber); if (sel) postModel({ type: 'select', pn: sel.partNumber }); }} title={tr('chat.clarification.viewModelSub', 'Interactive 3D')}
        style={{ flex: 1, border: `1px solid ${DIAGRAM_PALETTE.border}`, borderRadius: 10, background: DIAGRAM_PALETTE.bgSurface }} />
      <div data-testid="diagram-model-notice" role="note" title={tr('chat.clarification.ai3dBody', 'May not be 100% accurate. Verify against the original EPC illustration before confirming a part.')}
        style={{ ...pillBase, position: 'absolute', left: 12, top: 10, zIndex: 7, maxWidth: 'calc(100% - 24px)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 9px', borderRadius: 999, fontSize: 11.5, whiteSpace: 'nowrap' }}>
        <Sparkles size={13} aria-hidden style={{ color: DIAGRAM_PALETTE.navy, flexShrink: 0 }} />
        <strong style={{ fontWeight: 700, color: DIAGRAM_PALETTE.navy }}>{tr('chat.clarification.modelTitle', 'AI-generated 3D model')}</strong>
        <span style={{ color: DIAGRAM_PALETTE.textMuted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>· {tr('chat.clarification.ai3dShort', 'may be inaccurate — verify against the original')}</span>
        <button type="button" data-testid="diagram-model-view-original" onClick={(e) => { e.stopPropagation(); setViewModel(false); }}
          style={{ flexShrink: 0, border: 'none', background: DIAGRAM_PALETTE.tagBg, borderRadius: 999, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: DIAGRAM_PALETTE.navy }}>
          {tr('chat.clarification.viewOriginal', 'View original')}
        </button>
        <button type="button" data-testid="diagram-model-rebuild" title={tr('chat.clarification.rebuildModelSub', 'Build the model again from scratch')}
          onClick={(e) => { e.stopPropagation(); if (drawingUrl) startModel(drawingUrl, true, true); }}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', borderRadius: 999, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: DIAGRAM_PALETTE.textMuted }}>
          <RefreshCw size={11} aria-hidden />{tr('chat.clarification.rebuildModel', 'Rebuild')}
        </button>
      </div>
    </div>
  ) : (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      {renderSurface(sheet || { diagram_url: diagram?.diagram_url ?? null, callouts: diagram?.callouts || [] })}
      {multiSheet && (
        <>
          <button type="button" aria-label={tr('chat.clarification.prevSheet', 'Previous sheet')}
            disabled={sheetIdx <= sheets[0].index}
            onClick={(e) => { e.stopPropagation(); goSheet(sheetIdx - 1); }}
            style={{ ...pillBase, position: 'absolute', left: 10, top: '50%', marginTop: -16, width: 32, height: 32, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, opacity: sheetIdx <= sheets[0].index ? 0.35 : 1, zIndex: 6 }}>
            <ChevronLeft size={15} />
          </button>
          <button type="button" aria-label={tr('chat.clarification.nextSheet', 'Next sheet')}
            disabled={sheetIdx >= sheets[sheets.length - 1].index}
            onClick={(e) => { e.stopPropagation(); goSheet(sheetIdx + 1); }}
            style={{ ...pillBase, position: 'absolute', right: 10, top: '50%', marginTop: -16, width: 32, height: 32, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, opacity: sheetIdx >= sheets[sheets.length - 1].index ? 0.35 : 1, zIndex: 6 }}>
            <ChevronRight size={15} />
          </button>
        </>
      )}
      <div style={{ position: 'absolute', left: 12, bottom: 10, pointerEvents: 'none', zIndex: 6 }}>{renderLegend()}</div>
      {showing3d && (
        <div data-testid="diagram-3d-notice" role="note" title={tr('chat.clarification.ai3dBody', 'May not be 100% accurate. Verify against the original EPC illustration before confirming a part.')}
          style={{ ...pillBase, position: 'absolute', left: 12, top: 10, zIndex: 7, maxWidth: 'calc(100% - 24px)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 9px', borderRadius: 999, fontSize: 11.5, whiteSpace: 'nowrap' }}>
          <Sparkles size={13} aria-hidden style={{ color: DIAGRAM_PALETTE.navy, flexShrink: 0 }} />
          <strong style={{ fontWeight: 700, color: DIAGRAM_PALETTE.navy }}>{tr('chat.clarification.ai3dTitle', 'AI-generated illustration')}</strong>
          <span style={{ color: DIAGRAM_PALETTE.textMuted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>· {tr('chat.clarification.ai3dShort', 'may be inaccurate — verify against the original')}</span>
          <button type="button" data-testid="diagram-3d-view-original" onClick={(e) => { e.stopPropagation(); setView3d(false); }}
            style={{ flexShrink: 0, border: 'none', background: DIAGRAM_PALETTE.tagBg, borderRadius: 999, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: DIAGRAM_PALETTE.navy }}>
            {tr('chat.clarification.viewOriginal', 'View original')}
          </button>
        </div>
      )}
      {filterActive && (
        <div data-testid="diagram-filter-badge" role="status"
          style={{ ...pillBase, position: 'absolute', left: '50%', bottom: 44, transform: 'translateX(-50%)', zIndex: 7, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 6px 5px 12px', fontSize: 11.5, fontWeight: 600, color: DIAGRAM_PALETTE.navy, whiteSpace: 'nowrap', pointerEvents: 'auto' }}>
          {tr('chat.clarification.filteredHighlighted', 'Filtered — {{count}} parts highlighted', { count: String(visibleGroups.length) })}
          <button type="button" data-testid="diagram-filter-clear" onClick={(e) => { e.stopPropagation(); clearFilters(); }}
            style={{ borderWidth: 0, borderStyle: 'none', borderRadius: 999, background: DIAGRAM_PALETTE.bgSubtle, padding: '2px 8px', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: DIAGRAM_PALETTE.textPrimary, cursor: 'pointer' }}>
            {tr('chat.clarification.clearFilters', 'Clear filters')}
          </button>
        </div>
      )}
    </div>
  );

  const renderLegend = () => {
    const dot: React.CSSProperties = { width: 13, height: 13, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box' };
    const extra = (legendExtras || []).map(x => ({
      k: x.kind, label: x.label,
      swatch: x.kind === 'userPick' ? { ...dot, width: 18, borderRadius: 999, background: DIAGRAM_TAG.cart } : { ...dot, background: x.kind === 'inCart' ? DIAGRAM_TAG.cart : DIAGRAM_PALETTE.navy },
      icon: x.kind === 'userPick' ? null : <Check size={9} color="#fff" />,
    }));
    const items = [
      { k: 'sel', label: tr('chat.clarification.legend.selected', 'Selected'), swatch: { ...dot, background: DIAGRAM_TAG.heroBg, border: `2px solid ${DIAGRAM_TAG.haloStroke}` }, icon: null as React.ReactNode },
      ...extra,
    ];
    // No match scale here: every card in the list names its own level.
    return (
      <div className="diagram-question-legend" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 11.5, fontWeight: 600, color: '#555' }}>
        {items.map(({ k, label, swatch, icon }) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={swatch}>{icon}</span>
            {label}
          </span>
        ))}
      </div>
    );
  };

  // ── Parts list ─────────────────────────────────────────────────────────────
  const renderTranslated = (text: string | null | undefined, baseStyle: React.CSSProperties) => {
    if (!text) return null;
    const translated = translations[text] || preTranslations?.[text];
    const show = !!translated && translated.trim() !== '' && translated.trim() !== text.trim();
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={baseStyle} title={text}>{text}</span>
        {show && (
          <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#5a5a5a', lineHeight: 1.35, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{translated}</span>
            <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 500, color: PRIMARY, background: '#f0efff', borderRadius: 4, padding: '1px 5px', lineHeight: '15px', whiteSpace: 'nowrap' }}>
              {tr('chat.clarification.translated', 'Translated')}
            </span>
          </span>
        )}
      </span>
    );
  };

  // Evidence for a row's estimate: the user's own words the catalog row carries,
  // and whether the catalog backs, contradicts, or omits each stated side/position.
  const requested = useMemo(() => {
    const texts = (requestTexts || []).filter(Boolean);
    return {
      tokens: evidenceTokens(texts.join(' ')),
      classes: texts.reduce<PositionClasses>((acc, text) => {
        const parsed = positionClassesFromName(text);
        return { position: acc.position || parsed.position, side: acc.side || parsed.side };
      }, { position: null, side: null }),
    };
  }, [requestTexts]);
  type EvidenceItem = { k: string; text: string; gap?: boolean };
  const evidenceItems = (r: DiagramRow): EvidenceItem[] => {
    const items: EvidenceItem[] = [];
    const catalogText = [r.name, tx(r.name), r.description, tx(r.description)].filter(Boolean).join(' ');
    const shared = Array.from(evidenceTokens(catalogText)).filter(w => requested.tokens.has(w)).slice(0, 2);
    if (shared.length) items.push({ k: 'name', text: tr('chat.clarification.evidenceName', 'Name matches “{{words}}”', { words: shared.join(', ') }) });
    const catalogClasses = [...positionChipsFromExtra(r.extra_details), r.name || '', tx(r.name)].reduce<PositionClasses>((acc, text) => {
      const parsed = positionClassesFromName(text);
      return { position: acc.position || parsed.position, side: acc.side || parsed.side };
    }, { position: null, side: null });
    ([['side', 'chat.clarification.attrSide', 'side'], ['position', 'chat.clarification.attrPosition', 'position']] as const).forEach(([dim, attrKey, attrDef]) => {
      const want = requested.classes[dim];
      if (!want) return;
      const got = catalogClasses[dim];
      const show = (v: string) => { const [k, d] = POSITION_LABEL[v] || ['', v]; return k ? tr(k, d) : v; };
      if (got === want) items.push({ k: `${dim}-ok`, text: tr('chat.clarification.evidenceAttrMatch', '{{value}} — as you asked', { value: show(want) }) });
      else if (!got) items.push({ k: `${dim}-none`, gap: true, text: tr('chat.clarification.evidenceAttrMissing', 'Catalog states no {{attr}}', { attr: tr(attrKey, attrDef) }) });
      else items.push({ k: `${dim}-other`, gap: true, text: tr('chat.clarification.evidenceAttrOther', 'Catalog says {{value}}', { value: show(got) }) });
    });
    return items;
  };

  // Match strength (spec §2–§3): bars + one word. Never a percentage, and the
  // "why this estimate" evidence now rides in the indicator's own tooltip
  // instead of a separate ⓘ.
  const renderScore = (r: DiagramRow, onTint: boolean) => (r.confirmed ? null : (
    <MatchStrength level={matchLevel(r.score)} size="md" tr={tr} onTint={onTint}
      evidence={matchLevel(r.score) != null ? evidenceItems(r) : undefined} />
  ));

  // Every remaining catalog attribute as a collapsed key/value grid; OE remarks
  // join it as "Note" rows (any remark that separates two candidates is already
  // promoted onto the row by variantDifferences).
  const renderDetailChips = (rawEd?: Record<string, string> | null, mergedCount = 1) => {
    const ed: Record<string, string> = rawEd && typeof rawEd === 'object' ? rawEd : {};
    const details = catalogDetailEntries(ed).map(d => ({ label: tr(`chat.clarification.detail.${normalizeDetailKey(d.key)}`, d.label), value: tx(d.value) || d.value }));
    const seen = new Set<string>();
    for (const [k, v] of Object.entries(ed)) {
      if (!isNoteKey(k)) continue;
      const raw = String(v ?? '').trim();
      const norm = raw.toLowerCase();
      if (!raw || NULLISH.includes(norm) || seen.has(norm)) continue;
      seen.add(norm);
      details.push({ label: tr('chat.clarification.detail.note', 'Note'), value: tx(raw) || raw });
    }
    if (mergedCount > 1) details.push({ label: tr('chat.clarification.detail.catalogRecords', 'Catalog records'), value: tr('chat.clarification.catalogRecordsMerged', '{{count}} catalog records merged', { count: String(mergedCount) }) });
    if (!details.length) return null;
    return (
      <details className="diagram-catalog-details" onClick={(e) => e.stopPropagation()} style={{ marginTop: 1, minWidth: 0 }}>
        {/* Plain grey text with a turning chevron — not a button. */}
        <summary data-testid="catalog-details" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0, border: 'none', background: 'none', fontSize: 10.5, fontWeight: 500, color: DIAGRAM_PALETTE.textMuted, cursor: 'pointer', userSelect: 'none', listStyle: 'none' }}>
          {tr('chat.clarification.catalogDetails', 'Catalog details')} <ChevronDown size={11} aria-hidden className="diagram-catalog-details-chevron" />
        </summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(72px, auto) 1fr', columnGap: 8, rowGap: 2, marginTop: 3 }}>
          {details.map((d, i) => (
            <React.Fragment key={`det-${i}`}>
              <span style={{ fontSize: 10.5, color: '#9a9a9a', fontWeight: 500, overflowWrap: 'anywhere' }}>{d.label}</span>
              <span style={{ fontSize: 10.5, color: '#555', overflowWrap: 'anywhere', lineHeight: 1.4 }}>{d.value}</span>
            </React.Fragment>
          ))}
        </div>
      </details>
    );
  };

  // ── Tags ───────────────────────────────────────────────────────────────────
  // One tag row per part (functionality, answer, features, position — see
  // utils/partTags). A kit's header carries the tags true of the whole kit,
  // and the rows under it don't repeat them.
  const reqType = (requestedType || '').trim();
  const reqTypeShown = reqType ? (tx(reqType) || reqType) : '';
  const isRequestedRow = (r: DiagramRow): boolean => {
    if (!reqType) return false;
    const names = [r.name, tx(r.name)].filter((x): x is string => !!x);
    if (names.some(n => sameFunction(reqType, n) || sameFunction(reqTypeShown, n))) return true;
    if (!r.candidate) return false;
    // A candidate whose name mentions the requested kind is it ("Door" →
    // PANEL ASSY-FRONT DOOR), while a door HINGE, never a candidate, is not.
    if (names.some(n => mentionsFunction(reqType, n) || mentionsFunction(reqTypeShown, n))) return true;
    // A name in another language than the request can't be compared word for
    // word: the scorer's confident candidate stands in for it.
    return (matchLevel(r.score) ?? 0) >= 3;
  };
  const tagsByGroup = new Map<Group, PartTag[]>();
  const tagSourceTexts: string[] = [];
  for (const g of groups) {
    const r = g.top;
    const shownValues = (variantDifferences.get(g) || []).flatMap(d => [d.value, tx(d.value) || d.value]);
    const names = [r.name, tx(r.name), r.description, tx(r.description)].filter((x): x is string => !!x);
    const answer = rowAnswer?.(r) ?? null;
    const ai = rowAiTags?.(r, { shownValues, names }) ?? null;
    if (answer) tagSourceTexts.push(answer.label);
    tagSourceTexts.push(...(ai?.features || []));
    tagsByGroup.set(g, cardTags({
      texts: names,
      extra_details: r.extra_details,
      axes: r.axes ?? null,
      // No request (a search result) → no basis for green / red: no functionality tag.
      functionName: reqType ? functionOf(tx(r.name) || r.name || tx(r.description) || r.description) : '',
      requestedType: reqTypeShown,
      isRequested: isRequestedRow(r),
      answer: answer ? { ...answer, label: tx(answer.label) || answer.label } : null,
      isKit: isCatalogKit(r as any),
      ai: ai ? { ...ai, features: (ai.features || []).map(f => tx(f) || f) } : null,
    }));
  }
  // Tag and filter words that don't come from the language files (answer
  // labels, AI values, diagram names) are translated like the rows, so a
  // Spanish user reads Spanish chips. They arrive after the list is open
  // (the AI comparison lands later), so this batch never blocks the viewer.
  const tagTextKey = Array.from(new Set(tagSourceTexts
    .concat((sectionTabs || []).map(s => s.label))
    .concat(requestedType ? [requestedType] : [])
    .map(s => String(s || '').trim()).filter(Boolean))).join('\x01');
  useEffect(() => {
    if (!open || !tagTextKey) return;
    const missing = tagTextKey.split('\x01').filter(s => !translations[s] && !preTranslations?.[s] && !tagTried.has(s));
    if (!missing.length) return;
    const items = missing.map((text, i) => ({ key: String(i), text }));
    let cancelled = false;
    // Asked once per text: an answer (or a failure, or the cap) settles it,
    // and a text the service leaves as is then shows as it came.
    const settle = () => setTagTried(prev => { const next = new Set(prev); missing.forEach(m => next.add(m)); return next; });
    const cap = window.setTimeout(() => { if (!cancelled) settle(); }, 6000);
    TranslationBatchManager.request(String(getActiveOrderId() ?? ''), i18n.language || '', items)
      .then(res => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        items.forEach(it => { const v = res[it.key]; if (v) map[it.text] = v; });
        if (Object.keys(map).length) setTranslations(prev => ({ ...prev, ...map }));
      })
      .catch(() => { /* originals stay */ })
      .finally(() => { if (!cancelled) { window.clearTimeout(cap); settle(); } });
    return () => { cancelled = true; window.clearTimeout(cap); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tagTextKey, i18n.language]);
  // A diagram's name shows once it is final: the caller's name has landed
  // (the AI names the diagrams a moment after opening) and its translation
  // has settled. Until then the tab and its chip hold a spinner, so the name
  // never changes under the user; on a failure the original stays.
  const textSettled = (s: string) => !s || !!translations[s] || !!preTranslations?.[s] || tagTried.has(s);
  const sectionLabelOf = (tab: { label: string; pending?: boolean }): string | null => {
    const label = String(tab.label || '').trim();
    if (tab.pending || !textSettled(label)) return null;
    return tx(label) || label;
  };
  // Only a kit drawn as a kit (it lists its components) has a header.
  const kitHeaderOf = new Map<Group, PartTag[]>();
  const kitSkus = new Set<Group>();
  for (const c of kitClusters) {
    c.kits.forEach(g => kitSkus.add(g));
    if (!c.members.length) continue;
    const header = kitHeaderTags(c.kits.map(g => tagsByGroup.get(g) || []), c.members.map(g => tagsByGroup.get(g) || []), reqTypeShown);
    for (const g of [...c.kits, ...c.members]) kitHeaderOf.set(g, header);
  }
  /** A kit drawn with its components says how many it holds: "Kit · 3 parts". */
  const kitTagged = (g: Group, tags: PartTag[]): PartTag[] => {
    const kitTag = kitSkus.has(g) ? kitHeaderOf.get(g)?.find(t => t.kind === 'kit') : undefined;
    if (!kitTag) return tags;
    return [...tags.filter(t => t.kind !== 'kit'), kitTag];
  };
  /** Everything true of a card — its own tags and its kit header's. */
  const effectiveTags = (g: Group): PartTag[] => {
    const header = kitHeaderOf.get(g);
    return [...(header || []), ...withoutTags(tagsByGroup.get(g) || [], header, kitSkus.has(g))];
  };
  // Chips come from ALL the cards; which of them SHOW depends on the list
  // the active chips leave (see shownChips below).
  const allCards = groups.map(effectiveTags);
  const filterChips = filterTags(allCards, activeFilters);
  // An answer option filters by its own tag ("① Outer front bumper cover");
  // an answer made only of side words ("Left front") by those position tags.
  // The filter ids the last picked answer turned on.
  const answerIdsRef = useRef<string[]>([]);
  const answerFilterIds = (n: number, label: string): string[] => {
    const id = `answer:${n}`;
    if (groups.some(g => cardAnswersFilter(effectiveTags(g), id))) return [id];
    const vocab = vocabularyTagsOf(label);
    const ids = (vocab || []).map(v => filterId(v as PartTag));
    return ids.length && groups.some(g => ids.every(x => cardAnswersFilter(effectiveTags(g), x))) ? ids : [];
  };
  // An answer picked while another diagram was showing: once its diagram is
  // open (after the switch cleared the filters), turn its filter on there.
  const appliedAnswerRequest = useRef<number | null>(null);
  useEffect(() => {
    if (!answerRequest || answerRequest.sectionKey !== activeSectionKey) return;
    if (appliedAnswerRequest.current === answerRequest.nonce && activeFilters.size) return;
    const ids = answerFilterIds(answerRequest.n, answerRequest.label);
    if (!ids.length) return;
    appliedAnswerRequest.current = answerRequest.nonce;
    answerIdsRef.current = ids;
    setActiveFilters(new Set(ids));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerRequest?.nonce, activeSectionKey, diagram?.diagram_url, groups.length]);
  const headerCtx: DiagramHeaderCtx = {
    selectedTags: groups.filter(g => g.rows.some(r => r.selected)).map(g => ({ key: g.key, tags: kitTagged(g, effectiveTags(g)) })),
    answerActive: (n, label) => { const ids = answerFilterIds(n, label); return ids.length > 0 && ids.every(x => activeFilters.has(x)); },
    toggleAnswer: (n, label) => {
      const ids = answerFilterIds(n, label);
      if (!ids.length) return false;
      setActiveFilters(prev => {
        const next = new Set(prev);
        const on = ids.every(x => next.has(x));
        // One answer at a time: picking another replaces the previous one's filters.
        for (const x of answerIdsRef.current) next.delete(x);
        if (on) { ids.forEach(x => next.delete(x)); answerIdsRef.current = []; return next; }
        ids.forEach(x => next.add(x));
        answerIdsRef.current = ids;
        // An answer never empties the list. If the answer's filters plus the
        // chips already on leave nothing, the answer wins and the other chips
        // go: a question with no parts under its own answer must not render.
        const anyPass = (f: Set<string>) => groups.some(g => passesFilters(effectiveTags(g), f));
        return anyPass(next) ? next : new Set(ids);
      });
      return true;
    },
  };
  const filtersOn = activeFilters.size > 0;

  const q = partSearch.trim().toLowerCase();
  // ONE search box (utils/partTags searchMatches): exact on part numbers and
  // callout refs, word matching on phrases — over the name, its translation,
  // the description and the card's tags.
  const matchGroup = (g: Group): boolean => {
    if (!q) return true;
    const tagWords = effectiveTags(g).map(t => tagText(t, tr, true));
    return g.rows.some(r => searchMatches({
      texts: [r.name, tx(r.name), r.description, tx(r.description), ...tagWords],
      numbers: [r.partNumber, ...supersessionNumbers(r), ...supersessionValues(r)],
      callout: r.callout,
    }, partSearch));
  };
  // ONE stream in estimate order: a kit renders as a single section placed
  // where its best member falls in `groups` (already candidate-first, then by
  // estimate), so a low-match kit can't jump the queue ahead of the candidates.
  type ListEntry = { kitFamily: KitCluster; group?: undefined } | { kitFamily?: undefined; group: Group };
  const familyOf = (g: Group) => kitClusters.find(c => c.kits.includes(g) || c.members.includes(g));
  // The list for a given set of chips — the shown list, and (for each chip)
  // the list it would leave, so the chip counts are exactly what a click shows.
  const entriesFor = (filters: Set<string>): ListEntry[] => {
    const passFilters = (g: Group) => passesFilters(effectiveTags(g), filters);
    const out: ListEntry[] = [];
    const seen = new Set<Group>();
    for (const g of groups) {
      if (seen.has(g)) continue;
      const family = kitGroups.has(g) ? familyOf(g) : undefined;
      if (!family) { if (matchGroup(g) && passFilters(g)) out.push({ group: g }); continue; }
      const members = [...family.kits, ...family.members];
      members.forEach(m => seen.add(m));
      // Filtering keeps the kit's frame and only the parts that pass.
      const shown: KitCluster = filters.size > 0
        ? { kits: family.kits.filter(passFilters), members: family.members.filter(passFilters) }
        : family;
      if ([...shown.kits, ...shown.members].some(matchGroup)) out.push({ kitFamily: shown });
    }
    return out;
  };
  const groupsOf = (list: ListEntry[]): Group[] => list.flatMap(e => (e.group ? [e.group] : [...e.kitFamily.kits, ...e.kitFamily.members]));
  const entries: ListEntry[] = entriesFor(activeFilters);
  // A kit stays out of the collapsed "other" group (it is a real choice) — it
  // just no longer outranks the candidates, which sort first in `groups`.
  // ── One filter state, both panels ──────────────────────────────────────────
  // The chips and the search narrow the list AND the drawing: while anything
  // filters, callouts with no part left in the list fade out and a badge on
  // the drawing says how many parts are highlighted.
  const filterActive = !!q || filtersOn;
  const visibleGroups: Group[] = groupsOf(entries);
  // How many of the parts listed NOW each chip keeps: an active chip, the
  // list as it stands; any other, the list with it added (AND).
  const chipCounts = new Map<string, number>(filterChips.map(t => {
    const id = filterId(t);
    return [id, activeFilters.has(id) ? visibleGroups.length : groupsOf(entriesFor(new Set([...Array.from(activeFilters), id]))).length];
  }));
  // A chip that keeps none of the listed parts is gone, not greyed out:
  // "Right" on hides "Left" and every chip only the hidden parts wore. A chip
  // the remaining parts still wear stays ("Door handle" on keeps "Complete
  // assembly"), and the active chips always stay, to be switched off.
  // The other side of an active side ("Left" under "Right") is gone too, even
  // where a both-sides part would still answer it: it is the answer not taken.
  const activeAxes = new Set(Array.from(activeFilters).map(filterAxisOf).filter(Boolean));
  const shownChips = filterChips.filter(t => {
    const id = filterId(t);
    if (activeFilters.has(id)) return true;
    const axis = filterAxisOf(id);
    return !(axis && activeAxes.has(axis)) && (chipCounts.get(id) ?? 0) > 0;
  });
  const visibleCallouts = new Set(visibleGroups.flatMap(g => g.rows.map(r => String(r.callout))));
  const clearFilters = () => { setPartSearch(''); setActiveFilters(new Set()); };
  const isCandidateEntry = (e: ListEntry) => (e.group ? e.group.rows.some(isPrimaryRow) : true);
  const candidateEntries = entries.filter(isCandidateEntry);
  const otherEntries = entries.filter(e => !isCandidateEntry(e));

  const blocksOf = (gs: Group[]): LabelBlock[] => {
    const out: LabelBlock[] = [];
    const byName = new Map<string, LabelBlock>();
    for (const g of gs) {
      const raw = g.top.name || `#${g.top.callout}`;
      const k = `${raw.toLowerCase().replace(/\s+/g, ' ').trim()}::${g.top.callout}`;
      const s = typeof g.top.score === 'number' ? g.top.score : null;
      const hit = byName.get(k);
      if (hit) {
        hit.groups.push(g);
        if (s != null && (hit.score == null || s > hit.score)) { hit.score = s; hit.top = g; }
      } else {
        const b: LabelBlock = { name: raw, groups: [g], score: s, top: g };
        byName.set(k, b);
        out.push(b);
      }
    }
    return out;
  };

  const isRowActive = (g: Group) => !!active && g.rows.some(r => r.key === active);

  // The list header's count: parts that match high.
  const highCount = groups.filter(g => matchLevel(g.top.score) === 4).length;
  const selectedCount = selectedCountProp ?? groups.filter(g => g.top.selected).length;
  // The ONE row whose action stays filled: the selection, else the best match.
  const primaryKey: string | null = (() => {
    const sel = groups.find(g => g.rows.some(r => r.selected));
    if (sel) return sel.rows.find(r => r.selected)!.key;
    const best = [...groups].filter(g => matchLevel(g.top.score) != null)
      .sort((a, b) => (b.top.score ?? 0) - (a.top.score ?? 0))[0];
    return best ? best.top.key : null;
  })();

  // Every name the row displays (catalog + translated) — AI tags that only
  // repeat one of these are dropped by the caller.
  const rowNames = (r: DiagramRow): string[] =>
    [r.name, tx(r.name), r.description, tx(r.description)].filter((x): x is string => !!x);

  // Two or more parts under one label share the best match and NOTHING — no
  // catalog field, no AI difference — tells them apart: say so instead of
  // leaving the user to guess between identical-looking rows.
  const tiedWithoutDifference = (gs: Group[]): Group[] => {
    const levels = gs.map(g => matchLevel(g.top.score));
    const best = Math.max(...levels.map(l => l ?? -1));
    if (best < 0) return [];
    const tied = gs.filter((g, i) => levels[i] === best);
    if (tied.length < 2) return [];
    const differs = (g: Group) => (variantDifferences.get(g) || []).length > 0
      || !!rowHasDifference?.(g.top, { names: rowNames(g.top) });
    return tied.some(differs) ? [] : tied;
  };

  // One part number → one selectable row. `nested` = inside a multi-part label
  // card (several parts share the label's name); otherwise the row IS the
  // card and carries the label's ref and title itself.
  // Line 1 is the description, line 2 the part number — except in a shared
  // label with nothing that tells this part apart, where the number leads.
  type RowOpts = {
    /** Single-part card: the label's ref chip leads the title. */
    showRef?: boolean;
    /** Nested row: what tells this part apart from its siblings (null → the number leads). */
    lead?: React.ReactNode | null;
    /** The lead is the catalog fields that differ — don't repeat them below. */
    leadFromVariants?: boolean;
    /** "These 2 differ only by …" on the first of equally high parts. */
    diffHint?: React.ReactNode;
    /** The label header already shows the match every part shares. */
    hideScore?: boolean;
  };
  const renderPartRow = (g: Group, gi: number, nested: boolean, blockCallout: string, blockName: string, inherited?: PartTag[], opts: RowOpts = {}) => {
    const r = g.top;
    const tags = kitTagged(g, withoutTags(tagsByGroup.get(g) || [], inherited, kitSkus.has(g)));
    const tinted = r.selected || flashRow === r.key;
    const isSel = r.selected;
    const isActive = isRowActive(g);
    const isFlash = flashRow === r.key;
    const selBox = selectionMode && (
      <span style={{ flexShrink: 0, width: 20, height: 20, marginTop: 1, borderRadius: selectionMode === 'multi' ? 5 : 999, border: `2px solid ${isSel || r.candidate ? DIAGRAM_PALETTE.navy : r.name ? DIAGRAM_TAG.optStroke : DIAGRAM_TAG.naStroke}`, background: isSel ? DIAGRAM_PALETTE.navy : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {isSel && <Check size={13} color="#fff" />}
      </span>
    );
    // One quiet line under the tags: what tells this part number apart (bold)
    // unless it already leads the row, then the caller's extras and quantity.
    const bits: React.ReactNode[] = [];
    // Catalog values already on the row — the AI card skips tags that repeat them.
    const shownValues: string[] = [];
    for (const d of variantDifferences.get(g) || []) {
      shownValues.push(d.value, tx(d.value) || d.value);
      if (opts.leadFromVariants) continue;
      const generic = /^(special\s*)?(detail|details|note|notes|remark|remarks|info|comment|comments)\s*\d*$/i.test(d.label);
      const raw = tx(d.value) || d.value;
      const value = raw.length > 60 ? `${raw.slice(0, 59)}…` : raw;
      bits.push(<span key={`vd-${d.key}`} data-testid="variant-difference">{!generic && <>{tr(`chat.clarification.detail.${normalizeDetailKey(d.key)}`, d.label)}: </>}<strong>{value}</strong></span>);
    }
    (rowBits?.(r, nested) || []).forEach((b, i) => bits.push(<React.Fragment key={`x-${i}`}>{b}</React.Fragment>));
    // The quantity rides on the row's action ("Add ×2") when the caller draws
    // one; only a viewer without actions spells it out here.
    if (r.quantity && !renderRowAction) bits.push(<span key="qty">×{String(r.quantity)}</span>);
    const number = r.partNumber ? displayPartRef(r.partNumber) : null;
    // Single card: the catalog's own name, nothing added (a side folded in
    // was often only inherited from the section). Nested: the distinct lead.
    const ownTitle = partDescription(tx(r.name) || r.name, blockName)
      || (r.description ? (tx(r.description) || r.description) : '');
    const lead: React.ReactNode | null = nested ? (opts.lead || null) : (ownTitle || null);
    // Supersession sits under Catalog details; the row still SAYS it has
    // some, or two numbers that differ only in currency look interchangeable.
    const ss = hasSupersessionData(r) && (
      <span data-testid="ss-badge" title={tr('chat.clarification.hasSupersession', 'Has supersession data')}
        style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, border: `1px solid ${DIAGRAM_PALETTE.border}`, fontSize: 10, fontWeight: 700, color: DIAGRAM_PALETTE.textMuted, flexShrink: 0 }}>SS</span>
    );
    const numberLine = (leading: boolean) => (number || leading) && (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
        <span data-testid="part-number" style={leading
          ? { fontSize: 14, fontWeight: 700, color: DIAGRAM_PALETTE.textPrimary, fontFamily: 'Inter Tight, ui-monospace, monospace', overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }
          : { fontSize: 13, fontWeight: 600, color: DIAGRAM_PALETTE.textMuted, fontFamily: 'Inter Tight, ui-monospace, monospace', overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>
          {number || `#${r.callout}`}
        </span>
        {ss}
      </span>
    );
    // Drawn on another illustration: an "Illustration N" chip that brings that
    // drawing forward and lights the part's callout on it.
    const rowSheet = sheetOfRow(r);
    const offSheetN = multiSheet && rowSheet && rowSheet !== sheetIdx ? rowSheet : null;
    const illustrationChip = offSheetN != null && (
      <button type="button" data-testid="illustration-chip"
        title={tr('chat.clarification.showOnIllustration', 'Show on illustration {{n}}', { n: String(offSheetN) })}
        onClick={(e) => { e.stopPropagation(); goSheet(offSheetN); setActive(r.key); }}
        style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, border: `1px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface, fontSize: 10.5, fontWeight: 600, color: '#4a4856', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
        <Layers size={10} aria-hidden />{tr('chat.clarification.illustrationN', 'Illustration {{n}}', { n: String(offSheetN) })}
      </button>
    );
    // A single card's chip mirrors its tag on the drawing: navy once chosen.
    const chipPriority: CalloutPriority = opts.showRef && isSel && r.candidate ? 'hero' : r.priority;
    const showChip = opts.showRef || r.callout !== blockCallout;
    return (
      <div
        key={`${r.key}#${gi}`}
        ref={(el) => { for (const row of g.rows) { if (el) rowRefs.current.set(row.key, el); else rowRefs.current.delete(row.key); } }}
        className="diagram-part-row"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-pressed={isSel}
        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); choose(g); } }}
        onClick={() => choose(g)}
        onMouseEnter={() => setActive(r.key)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: nested ? '8px 14px 8px 12px' : '10px 14px 10px 12px',
          borderTop: nested ? `0.5px solid ${DIAGRAM_PALETTE.border}` : undefined,
          background: isFlash ? DIAGRAM_PALETTE.lilac : isSel ? DIAGRAM_PALETTE.selBg : isActive ? DIAGRAM_PALETTE.bgSubtle : 'transparent',
          boxShadow: isFlash ? `inset 0 0 0 2px ${DIAGRAM_PALETTE.navy}` : isSel && nested ? `inset 3px 0 0 ${DIAGRAM_PALETTE.navy}` : undefined,
          transition: 'background 0.3s ease, box-shadow 0.3s ease',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {selBox}
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            {showChip && (
              <DiagramRefChip number={r.callout || '·'} priority={chipPriority} level={chipPriority !== 'hero' ? matchLevel(r.score) : null} inCart={r.inCart} />
            )}
            {!nested && r.name
              // The label's own title, once: the catalog name and, under it,
              // its translation (as the old label header showed it).
              ? <span data-testid="part-description" style={{ flex: 1, minWidth: 0 }}>
                  {renderTranslated(r.name, { fontSize: 14, fontWeight: 700, color: DIAGRAM_PALETTE.textPrimary, lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' })}
                </span>
              : lead
                ? <span data-testid="part-description" style={{ flex: 1, fontSize: 14, fontWeight: 700, color: DIAGRAM_PALETTE.textPrimary, lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 0 }}>{lead}</span>
                : <span style={{ flex: 1, minWidth: 0 }}>{numberLine(true)}</span>}
            {illustrationChip}
          </span>
          {lead && numberLine(false)}
          <PartTagRow tags={tags} tr={tr} requestedType={reqTypeShown} />
          {renderBlockNote?.(r)}
          {bits.length > 0 && (
            <span style={{ fontSize: 11, color: DIAGRAM_PALETTE.textMuted, lineHeight: 1.35, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
              {bits.map((b, i) => <React.Fragment key={i}>{i > 0 && ' · '}{b}</React.Fragment>)}
            </span>
          )}
          {opts.diffHint}
          {renderRowInsight?.(r, { shownValues, names: rowNames(r) })}
          {/* Supersession, alternatives, packaging and packed sizes live under
              Catalog details; the SS badge is all the row shows of them. */}
          {renderDetailChips(r.extra_details, g.duplicateCount)}
        </span>
        {/* Fixed 92px column so the word never jitters row to row. */}
        {!opts.hideScore && <span className="match-strength-col">{renderScore(r, tinted)}</span>}
        {renderRowAction && (
          <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, minHeight: 24 }} onClick={(e) => e.stopPropagation()}>
            {renderRowAction(r, { primary: primaryKey != null && g.rows.some(x => x.key === primaryKey) })}
          </span>
        )}
      </div>
    );
  };

  // What tells a part apart from the others sharing its label's name: its own
  // catalog description when the siblings' differ, else the catalog fields
  // whose values differ. Null when nothing does — the number then leads.
  const leadOf = (g: Group, siblings: Group[]): { text: React.ReactNode | null; fromVariants: boolean } => {
    const r = g.top;
    const norm = (s: string | null | undefined) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const desc = (tx(r.description) || r.description || '').trim();
    const names = [r.name, tx(r.name)].map(norm);
    const others = siblings.filter(x => x !== g).map(x => norm(tx(x.top.description) || x.top.description));
    if (desc && !names.includes(norm(desc)) && !others.includes(norm(desc))) return { text: desc, fromVariants: false };
    const vd = variantDifferences.get(g) || [];
    if (vd.length) {
      // Each differing field keeps its own mark (what the comparison reads).
      const text = vd.map((d, i) => {
        const generic = /^(special\s*)?(detail|details|note|notes|remark|remarks|info|comment|comments)\s*\d*$/i.test(d.label);
        const raw = tx(d.value) || d.value;
        const value = raw.length > 60 ? `${raw.slice(0, 59)}…` : raw;
        return (
          <React.Fragment key={`lead-${d.key}`}>
            {i > 0 && ' · '}
            <span data-testid="variant-difference">{!generic && <>{tr(`chat.clarification.detail.${normalizeDetailKey(d.key)}`, d.label)}: </>}{value}</span>
          </React.Fragment>
        );
      });
      return { text: <>{text}</>, fromVariants: true };
    }
    return { text: null, fromVariants: false };
  };

  // Equally HIGH parts under one label: say what differs between them, once,
  // on the first of them — the catalog fields that differ and the AI
  // comparison's dimension — with a way into the side-by-side comparison.
  const diffHintFor = (gs: Group[]): { first: Group; node: React.ReactNode } | null => {
    const high = gs.filter(g => matchLevel(g.top.score) === 4);
    if (high.length < 2) return null;
    const dims: string[] = [];
    const add = (s: string) => { const v = s.trim(); if (v && !dims.some(d => d.toLowerCase() === v.toLowerCase())) dims.push(v); };
    for (const g of high) for (const d of variantDifferences.get(g) || []) {
      const generic = /^(special\s*)?(detail|details|note|notes|remark|remarks|info|comment|comments)\s*\d*$/i.test(d.label);
      add(generic ? tr('chat.clarification.detail.note', 'Note') : tr(`chat.clarification.detail.${normalizeDetailKey(d.key)}`, d.label));
    }
    if (diffDimension) add(tx(diffDimension) || diffDimension);
    if (!dims.length) return null;
    return {
      first: high[0],
      node: (
        <span data-testid="diff-hint" style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, alignSelf: 'flex-start', marginTop: 4, padding: '2px 9px', borderRadius: 8, border: `1px dashed ${DIAGRAM_PALETTE.indigo}`, background: '#F7F6FF', color: DIAGRAM_PALETTE.indigo, fontSize: 11.5, fontWeight: 600, lineHeight: 1.45 }}>
          <Hash size={11} aria-hidden />
          {tr('chat.clarification.diffHint', 'These {{count}} differ only by {{what}}', { count: String(high.length), what: dims.join(', ') })}
          {onCompare && (
            <button type="button" data-testid="diff-hint-compare" onClick={(e) => { e.stopPropagation(); onCompare(); }}
              style={{ borderWidth: 0, borderStyle: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 700, color: DIAGRAM_PALETTE.indigo, cursor: 'pointer', textDecoration: 'underline' }}>
              — {tr('chat.clarification.diffHintCompare', 'compare')}
            </button>
          )}
        </span>
      ),
    };
  };

  // One catalog LABEL → one card. A label with ONE part is that part's row
  // (title once, no header). Several parts sharing the name sit under a header
  // naming the label; each row leads with what tells it apart. Match, tags
  // and notes are on the parts — what the user selects — never the label.
  const renderBlock = (b: LabelBlock, bi: number, inherited?: PartTag[]) => {
    const top = b.top.top;
    const n = b.groups.length;
    const anySel = b.groups.some(g => g.top.selected);
    const blockCallout = top.callout;
    const candidate = b.groups.some(g => g.top.candidate);
    const single = n === 1 ? b.groups[0] : null;
    const hint = n > 1 ? diffHintFor(b.groups) : null;
    // Parts of one label at the same match level: the level once, on the
    // label, instead of on every row.
    const levels = n > 1 ? b.groups.map(g => matchLevel(g.top.score)) : [];
    const sharedScore = n > 1 && levels[0] != null && levels.every(l => l === levels[0]);
    return (
      <section key={`blk:${bi}:${top.key}`} className="diagram-label-block" data-single={single ? 'true' : undefined}
        style={{
          // Each label is its own card; the chosen one has a blue border.
          border: `1px solid ${anySel ? DIAGRAM_PALETTE.navy : DIAGRAM_PALETTE.border}`,
          borderRadius: 12, overflow: 'hidden', marginBottom: 12,
          background: DIAGRAM_PALETTE.bgSurface,
          boxShadow: anySel ? `0 0 0 3px ${DIAGRAM_PALETTE.selBg}` : undefined,
        }}>
        {single ? renderPartRow(single, bi * 1000, false, blockCallout, b.name, inherited, { showRef: true }) : (
          <>
            <div data-testid="label-header"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                background: anySel ? DIAGRAM_PALETTE.selBg : DIAGRAM_PALETTE.bgSubtle,
                borderBottom: `1px solid ${anySel ? DIAGRAM_PALETTE.navy : DIAGRAM_PALETTE.border}`,
              }}
              onMouseEnter={() => setActive(top.key)}
            >
              <DiagramRefChip
                number={blockCallout || '·'}
                priority={anySel && top.candidate ? 'hero' : top.priority}
                level={null}
                inCart={b.groups.some(g => g.rows.some(r => r.inCart))}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                {renderTranslated(b.name, { fontSize: 13, color: DIAGRAM_PALETTE.textPrimary, fontWeight: candidate ? 700 : 500, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 })}
              </span>
              {sharedScore && <span className="match-strength-col" data-testid="label-match">{renderScore(top, anySel)}</span>}
            </div>
            {(() => {
              const tied = tiedWithoutDifference(b.groups);
              if (!tied.length) return null;
              return (
                <div data-testid="tied-no-difference" role="note"
                  style={{ padding: '8px 12px', fontSize: 11.5, lineHeight: 1.45, color: '#7c570f', background: '#fffbeb', borderBottom: `1px solid ${DIAGRAM_PALETTE.border}` }}>
                  {tr('chat.clarification.tiedNoDifference', 'These {{count}} parts match equally and the catalog shows no difference between them. Check the engine or chassis number and the Catalog details before choosing.', { count: String(tied.length) })}
                </div>
              );
            })()}
            {b.groups.map((g, i) => {
              const lead = leadOf(g, b.groups);
              return renderPartRow(g, bi * 1000 + i, true, blockCallout, b.name, inherited, {
                lead: lead.text, leadFromVariants: lead.fromVariants,
                diffHint: hint && hint.first === g ? hint.node : undefined,
                hideScore: sharedScore,
              });
            })}
          </>
        )}
      </section>
    );
  };

  // Consecutive plain groups still merge by name into one label block; a kit
  // section breaks the run and renders in place.
  const renderEntries = (list: ListEntry[], base: number): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    let run: Group[] = [];
    let n = 0;
    // "VERY LOW · N" threshold divider before the long tail, so the list stays
    // ONE descending order (match sort only — ref order has no tail).
    let dividerShown = false;
    const veryLowCount = sortMode === 'match'
      ? list.reduce((acc, e) => acc + (e.group ? blocksOf([e.group]).filter(b => matchLevel(b.score) === 1).length : 0), 0)
      : 0;
    const flush = () => {
      if (!run.length) return;
      const gs = run;
      run = [];
      for (const b of blocksOf(gs)) {
        if (veryLowCount > 0 && !dividerShown && matchLevel(b.score) === 1) {
          dividerShown = true;
          out.push(
            <div key={`vlow:${base}`} data-testid="very-low-divider" role="separator"
              style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 2px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-secondary, #5F5C57)' }}>
              <span style={{ flex: 1, height: 1, background: DIAGRAM_PALETTE.border }} />
              {tr('matchLevel.veryLowDivider', 'Very low')} · {veryLowCount}
              <span style={{ flex: 1, height: 1, background: DIAGRAM_PALETTE.border }} />
            </div>,
          );
        }
        out.push(renderBlock(b, base + n++));
      }
    };
    list.forEach((e, i) => {
      if (e.group) { run.push(e.group); return; }
      flush();
      const k = e.kitFamily;
      // The kit and the parts it holds stay together in one frame, the parts
      // indented under it. No banner and no "included in the kit" line: the
      // kit's card says "Kit · N parts" and its Catalog details list them.
      const kitBlocks = blocksOf(k.kits).map(b => renderBlock(b, base + n++));
      if (!k.members.length) { out.push(...kitBlocks); return; }
      out.push(
        <section key={`kit:${base}:${i}`} data-testid="kit-family" aria-label={tr('chat.clarification.catalogKit', 'Catalog kit')} style={{ margin: 8 }}>
          {kitBlocks}
          <div style={{ marginLeft: 14, paddingLeft: 8, borderLeft: '2px solid #dbd8ff' }}>
            {blocksOf(k.members).map(b => renderBlock(b, base + n++))}
          </div>
        </section>,
      );
    });
    flush();
    return out;
  };

  const renderPartsList = () => (
    <div style={{ flex: 1, minWidth: 0, border: `0.5px solid ${DIAGRAM_PALETTE.border}`, borderRadius: 12, minHeight: 0, overflow: 'auto', background: DIAGRAM_PALETTE.bgSurface }}>
      {/* The search box and the chips stay above the list together. */}
      <div data-testid="list-filters" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
      <div style={{ padding: '9px 12px', background: DIAGRAM_PALETTE.bgSubtle, borderBottom: `0.5px solid ${DIAGRAM_PALETTE.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: DIAGRAM_PALETTE.textMuted, pointerEvents: 'none' }} />
          <input
            type="text"
            value={partSearch}
            onChange={(e) => setPartSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={tr('chat.clarification.searchParts', 'Search parts…')}
            aria-label={tr('chat.clarification.searchParts', 'Search parts…')}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 26px 6px 28px', fontSize: 12, borderRadius: 8, outline: 'none', border: `1px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface, color: DIAGRAM_PALETTE.textPrimary }}
          />
          {partSearch && (
            <button type="button" onClick={() => setPartSearch('')} aria-label={tr('common.clear', 'Clear')}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', border: 'none', background: 'transparent', cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, padding: 2 }}>
              <X size={13} />
            </button>
          )}
        </div>
      </div>
      <FilterChips tags={shownChips} counts={chipCounts} active={activeFilters} onToggle={toggleFilter} onClear={() => setActiveFilters(new Set())} tr={tr}
        sections={sectionTabs?.map(s => ({ ...s, label: sectionLabelOf(s) }))} activeSection={activeSectionKey} onSelectSection={onSelectSection} />
      {onClearSelection && selectedCount > 0 && !disabled && (
        <div data-testid="selection-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: DIAGRAM_PALETTE.selBg, borderBottom: `0.5px solid ${DIAGRAM_PALETTE.border}` }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: DIAGRAM_PALETTE.navy }}>
            <Check size={13} aria-hidden />
            {tr('chat.clarification.selectedCount', '{{count}} selected', { count: String(selectedCount) })}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" data-testid="clear-selection" onClick={(e) => { e.stopPropagation(); onClearSelection(); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 4px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, color: DIAGRAM_PALETTE.navy, textDecoration: 'underline', textUnderlineOffset: 2 }}>
            <X size={12} aria-hidden />{tr('chat.clarification.clearSelection', 'Clear selection')}
          </button>
        </div>
      )}
      </div>

      {!q && renderListIntro?.()}

      {renderEntries(candidateEntries, 0)}

      {(q || filtersOn) && entries.length === 0 && (
        <div style={{ padding: '14px 12px', fontSize: 12, color: DIAGRAM_PALETTE.textMuted, textAlign: 'center' }}>
          {q ? tr('chat.clarification.noPartsMatch', 'No parts match your search') : tr('chat.clarification.noPartsMatchFilters', 'No parts match these filters')}
          {renderEmptyAction && <div data-testid="empty-action" style={{ marginTop: 10 }}>{renderEmptyAction()}</div>}
        </div>
      )}

      {/* The non-candidate parts, collapsible; force-open while searching or filtering. */}
      {otherEntries.length > 0 && (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setShowOther(v => !v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowOther(v => !v); } }}
            aria-expanded={showOther || !!q || filtersOn}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px 6px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: DIAGRAM_PALETTE.textMuted, background: DIAGRAM_PALETTE.bgSurface, borderBottom: `0.5px solid ${DIAGRAM_PALETTE.border}`, cursor: 'pointer', userSelect: 'none' }}
          >
            <ChevronDown size={12} style={{ transform: (showOther || q || filtersOn) ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
            {tr('chat.clarification.otherParts', 'Other parts in this diagram')}
            <span style={{ fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}>· {otherEntries.length}</span>
          </div>
          {(showOther || !!q || filtersOn) && renderEntries(otherEntries, 1000000)}
        </>
      )}

      {!q && unavailableRefs.length > 0 && (
        <div data-testid="diagram-unavailable-note"
          style={{ padding: '10px 12px', fontSize: 11.5, lineHeight: 1.45, color: DIAGRAM_PALETTE.textMuted, borderTop: `0.5px solid ${DIAGRAM_PALETTE.border}` }}>
          {tr('imageWithCallouts.notAvailableNote',
            '{{n}} part(s) on this diagram are not available — ref. {{refs}}, marked with a dashed outline and slash (⊘). Not selectable.',
            { n: unavailableRefs.length, refs: unavailableRefs.join(', ') })}
        </div>
      )}
    </div>
  );

  // ── Category row + illustration tabs ──────────────────────────────────────
  // The question's own diagrams (front / rear panel) and the other categories
  // the resolver says may hold the part, as ONE list: "Category 1 of N".
  type CategoryPick = CategoryItem & { tabKey?: string; candidate?: DiagramCandidateSection };
  const categoryItems: CategoryPick[] | null = (() => {
    const tabs: CategoryPick[] = sectionTabs && sectionTabs.length > 1
      ? sectionTabs.map(t => ({ key: `tab:${t.key}`, name: sectionLabelOf(t) ?? t.label, count: t.count ?? null, current: t.key === activeSectionKey, tabKey: t.key }))
      : [];
    // Other categories only when one can be opened from here.
    if (!onPickCandidate || !onOpenCandidates) return tabs;
    if (candidateSections == null) return tabs.length ? tabs : null;
    const tabKeys = new Set(tabs.map(t => t.tabKey));
    const cands: CategoryPick[] = candidateSections
      .filter(c => !tabKeys.has(`${c.group_id}/${c.section_id}`))
      .filter(c => !(tabs.length && c.current))
      .map(c => ({ key: `cand:${c.group_id}/${c.section_id}`, name: c.name, parent: c.parent_group ?? null, count: c.count ?? null, current: !!c.current, candidate: c }));
    return [...tabs, ...cands];
  })();
  // A drawing with no title in the catalog is named by what it shows: the
  // kinds of part on its callouts, best match first ("Mirror arm, base, glass").
  const illustrationTitle = (sh: DiagramSheet): string | null => {
    const on = new Set((sh.part_callouts && sh.part_callouts.length ? sh.part_callouts : (sh.callouts || []).map(c => String(c.label))).map(String));
    const names: string[] = [];
    const seen = new Set<string>();
    const ordered = [...listRows].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    for (const r of ordered) {
      if (!on.has(String(r.callout))) continue;
      const fn = functionOf(tx(r.name) || r.name);
      const k = fn.toLowerCase();
      if (!fn || seen.has(k)) continue;
      seen.add(k);
      names.push(names.length ? k : fn);
      if (names.length === 3) break;
    }
    return names.length ? names.join(', ') : null;
  };
  const renderCategoryRow = () => {
    const name = sectionName || diagram?.section_name || '';
    if (!name && !(sectionTabs && sectionTabs.length > 1)) return null;
    const current = sectionTabs?.find(t => t.key === activeSectionKey);
    const ids = sectionCode || (activeSectionKey ? activeSectionKey.split('/').join(' / ') : null);
    return (
      <CategorySwitcher
        tr={tr}
        parent={groupName || diagram?.group_name || null}
        name={name || (current ? (sectionLabelOf(current) ?? current.label) : '')}
        code={ids}
        categories={categoryItems}
        onPick={(c) => {
          const pick = c as CategoryPick;
          if (pick.tabKey) onSelectSection?.(pick.tabKey);
          else if (pick.candidate) onPickCandidate?.(pick.candidate);
        }}
        onSearchCatalog={onSearchCatalog}
        illustrations={multiSheet ? sheets.map(sh => ({ index: sh.index, title: sh.title || illustrationTitle(sh), code: sh.code ?? null })) : []}
        activeIllustration={sheetIdx}
        onIllustration={(n) => goSheet(n)}
      />
    );
  };

  // ── Shell ──────────────────────────────────────────────────────────────────
  // The whole-screen spinner is for the FIRST open only. Moving to another
  // diagram afterwards keeps the frame (header, diagram chips, footer) and
  // spins just the pane that is still loading — the drawing until it has a
  // url, the parts list until its catalog strings are translated.
  const host = portalHostRef.current;
  if (!open) shownOnce.current = false;
  if (!open || !host) return null;
  const backdrop = (children: React.ReactNode) => createPortal(
    <div translate="no" className="fixed inset-0 notranslate diagram-question-backdrop"
      style={{ position: 'fixed', inset: 0, zIndex: 2147483647, background: DIAGRAM_PALETTE.backdrop, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2.5vh 2.5vw' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {children}
    </div>,
    host,
  );
  const noDrawing = !diagram || !(sheet?.diagram_url || diagram.diagram_url);
  if ((noDrawing || translating || !firstSettled) && !shownOnce.current) {
    return backdrop(
      <div style={{ background: DIAGRAM_PALETTE.bgPage, borderRadius: 16, boxShadow: '0 8px 32px rgba(32,30,26,0.18)', width: '95vw', height: '95vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: DIAGRAM_PALETTE.textMuted }}>
        <Loader2 size={28} className="animate-spin" aria-label={tr('chat.clarification.diagramLoading', 'Preparing diagram…')} />
      </div>,
    );
  }
  shownOnce.current = true;
  const paneSpinner = (testId: string) => (
    <div data-testid={testId} role="status" style={{ flex: 1, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: DIAGRAM_PALETTE.textMuted }}>
      <Loader2 size={22} className="animate-spin" aria-label={tr('chat.clarification.diagramLoading', 'Preparing diagram…')} />
    </div>
  );
  return backdrop(
    <div
      className={`tap-pad diagram-question-layout${showPartsTable ? ' has-parts-list' : ''}`}
      style={{
        position: 'relative', background: DIAGRAM_PALETTE.bgPage, borderRadius: 16, boxShadow: '0 8px 32px rgba(32,30,26,0.18)',
        width: '95vw', height: '95vh', display: 'grid',
        gridTemplateColumns: showPartsTable ? 'minmax(0, 1fr) minmax(300px, 460px)' : 'minmax(0, 1fr)',
        gridTemplateRows: 'auto minmax(0, 1fr) auto', overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close stays in the modal's top corner. On a wide screen the parts panel
          carries its own, up there too; on a phone that panel sits below the
          switch, so this one takes over (CSS picks). */}
      <button type="button" className="diagram-question-close" aria-label={tr('chat.clarification.close', 'Close')} onClick={onClose}
        style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, border: `1px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface, borderRadius: 8, cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, display: 'flex', padding: 6, boxShadow: '0 2px 8px rgba(32,30,26,0.10)' }}
      ><X size={16} /></button>
      {/* With no parts list the close button floats over this corner. The inset
          goes on the header's own padding (CSS), not this cell, so its tint
          still runs to the edge. */}
      <div className="diagram-question-header-cell" style={{ gridColumn: 1, gridRow: 1, minWidth: 0, background: '#fff', borderBottom: `1px solid ${DIAGRAM_PALETTE.border}` }}>
        {renderHeader(headerCtx)}
        {/* The question block's last lines: the category and its illustrations. */}
        <div style={{ padding: '0 16px 8px' }}>{renderCategoryRow()}</div>
      </div>
      {/* Phones show ONE pane at a time — a drawing and a list sharing a phone
          screen leaves neither usable. Hidden by CSS on wider screens, where the
          two sit side by side. */}
      <div className="diagram-question-switch" style={{ display: 'none', padding: '8px 10px 0', minWidth: 0, gap: 6 }}>
        {([
          { on: false, icon: <MapIcon size={14} />, label: tr('chat.clarification.diagramTab', 'Diagram') },
          { on: true, icon: <List size={14} />, label: tr('chat.clarification.partsList', 'Parts list') },
        ]).map(opt => (
          <button key={String(opt.on)} type="button" aria-pressed={showPartsTable === opt.on} onClick={() => setShowPartsTable(opt.on)}
            style={{
              flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
              border: `1px solid ${showPartsTable === opt.on ? PRIMARY : DIAGRAM_PALETTE.border}`,
              background: showPartsTable === opt.on ? '#f3f2ff' : DIAGRAM_PALETTE.bgSurface,
              color: showPartsTable === opt.on ? PRIMARY : DIAGRAM_PALETTE.textMuted,
            }}
          >{opt.icon}{opt.label}</button>
        ))}
      </div>
      <div className="diagram-question-body" style={{ gridColumn: 1, gridRow: 2, minHeight: 0, display: 'flex', alignItems: 'stretch', overflow: 'hidden' }}>
        <div className="diagram-question-surface-pane" style={{ position: 'relative', flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, padding: 16, overflow: 'hidden', background: DIAGRAM_PALETTE.bgSurface }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            {/* The 2D / 3D toggle on the right says which picture is on screen;
                the AI one also says so on the drawing itself. */}
            <span />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {(render3d || build3dModel) && !noDrawing && renderViewToggle()}
            <div style={{ display: 'flex', alignItems: 'center', background: DIAGRAM_PALETTE.bgSurface, border: `1px solid ${DIAGRAM_PALETTE.border}`, borderRadius: 8, padding: 3 }}>
              {/* Named, not just an icon: on a phone the list starts folded, so
                  this is the only way back to it. */}
              <button type="button" className="diagram-question-listbtn" aria-label={tr('chat.clarification.partsList', 'Parts list')} aria-pressed={showPartsTable} title={tr('chat.clarification.partsList', 'Parts list')} onClick={() => setShowPartsTable(v => !v)}
                style={{ border: 'none', background: showPartsTable ? '#f3f2ff' : 'transparent', borderRadius: 6, cursor: 'pointer', color: showPartsTable ? PRIMARY : DIAGRAM_PALETTE.textMuted, display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
              ><List size={16} />{tr('chat.clarification.partsList', 'Parts list')}</button>
              <div style={{ width: 1, height: 16, background: DIAGRAM_PALETTE.border, margin: '0 2px' }} />
              {renderZoomControls()}
            </div>
            </div>
          </div>
          {noDrawing ? paneSpinner('diagram-pane-loading') : renderViewport()}
          {!noDrawing && render3dStatus()}
          {!noDrawing && renderOverlay?.()}
        </div>
      </div>
      {showPartsTable && (
        <div className="diagram-question-parts" style={{ gridColumn: 2, gridRow: '1 / 4', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderLeft: `0.5px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgPage }}>
          <div data-testid="parts-list-header" style={{ padding: '12px 12px 12px 14px', borderBottom: `0.5px solid ${DIAGRAM_PALETTE.border}`, flexShrink: 0, background: DIAGRAM_PALETTE.bgSurface, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            {/* Basis 0: the title column gives way first, so the buttons stay on its row. */}
            <span style={{ flex: '1 1 0', minWidth: 96, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: DIAGRAM_PALETTE.textPrimary, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{tr('chat.clarification.partsList', 'Parts list')}</span>
              {/* How many parts the list holds, and how many match high. */}
              <span data-testid="parts-list-count" style={{ fontSize: 12.5, color: DIAGRAM_PALETTE.textMuted, fontWeight: 500 }}>
                <span style={{ whiteSpace: 'nowrap' }}>· {filterActive
                  ? <span data-testid="parts-list-shown"><strong style={{ color: DIAGRAM_PALETTE.navy, fontWeight: 700 }}>{tr('chat.clarification.partsShownOf', '{{shown}} of {{count}} parts', { shown: String(visibleGroups.length), count: String(groups.length) })}</strong></span>
                  : tr('chat.clarification.partsCount', '{{count}} parts', { count: String(groups.length) })}</span>
                {highCount > 0 && (
                  <> <span style={{ whiteSpace: 'nowrap' }}>· <strong style={{ color: DIAGRAM_PALETTE.navy, fontWeight: 700 }}>{tr('chat.clarification.highMatchCount', '{{count}} high match', { count: String(highCount) })}</strong></span></>
                )}
              </span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
              {whatsDifferent && (
                <button type="button" data-testid="whats-different" onClick={(e) => { e.stopPropagation(); whatsDifferent.onOpen(); }}
                  aria-busy={whatsDifferent.loading || undefined}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 14px', borderRadius: 10, borderWidth: 0, borderStyle: 'none', background: DIAGRAM_PALETTE.navy, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 2px 8px rgba(30,13,184,0.28)' }}>
                  {whatsDifferent.loading ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Hash size={15} strokeWidth={2.6} aria-hidden />}
                  {tr('chat.clarification.whatsDifferent', "What's different?")}
                </button>
              )}
              {/* Sort: "≡ Match ▾" — a native select under a styled face, so it
                  stays keyboard- and screen-reader-native. */}
              <label data-testid="match-sort" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5, height: 36, padding: '0 10px', borderRadius: 10, border: `1px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface, fontSize: 13.5, fontWeight: 600, color: DIAGRAM_PALETTE.textPrimary, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <ListFilter size={15} aria-hidden="true" />
                <span>{sortMode === 'match' ? tr('matchLevel.sortMatch', 'Match') : tr('matchLevel.sortRef', 'Catalog order')}</span>
                <ChevronDown size={13} aria-hidden="true" />
                <select
                  aria-label={tr('matchLevel.sortLabel', 'Sort list by')}
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as 'match' | 'ref')}
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', fontSize: 12 }}
                >
                  <option value="match">{tr('matchLevel.sortMatch', 'Match')}</option>
                  <option value="ref">{tr('matchLevel.sortRef', 'Catalog order')}</option>
                </select>
              </label>
              <button type="button" className="diagram-question-parts-close" aria-label={tr('chat.clarification.close', 'Close')} onClick={onClose}
                style={{ border: `1px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface, borderRadius: 10, cursor: 'pointer', color: DIAGRAM_PALETTE.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, padding: 0 }}
              ><X size={17} /></button>
            </span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 12, display: 'flex', flexDirection: 'column' }}>
            {translating ? (
              // The diagram chips stay put, so the user sees which diagram is loading.
              <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', border: `0.5px solid ${DIAGRAM_PALETTE.border}`, borderRadius: 12, overflow: 'hidden', background: DIAGRAM_PALETTE.bgSurface }}>
                <FilterChips tags={[]} active={activeFilters} onToggle={toggleFilter} onClear={() => setActiveFilters(new Set())} tr={tr}
                  sections={sectionTabs?.map(s => ({ ...s, label: sectionLabelOf(s) }))} activeSection={activeSectionKey} onSelectSection={onSelectSection} />
                {paneSpinner('parts-pane-loading')}
              </div>
            ) : renderPartsList()}
          </div>
        </div>
      )}
      <div className="diagram-question-footer" style={{ gridColumn: 1, gridRow: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8, padding: '12px 16px', background: DIAGRAM_PALETTE.bgSurface, borderTop: `0.5px solid ${DIAGRAM_PALETTE.border}`, minWidth: 0 }}>
        {renderFooter({ close: onClose })}
      </div>
    </div>,
  );
};

export default DiagramModal;
